<template>
  <div class="edit home" ref="editorRoot">
    <!-- Sticky header met titel en knop -->
    <div class="sticky-header">
      <div class="sticky-header-left">
        <button type="button" class="system-button" @click="$router.push(newModel ? '/cms/models/new' : `/cms/models/${$route.params.id}`)">
          <span class="btn-text">Oude editor</span>
        </button>
        <h1>{{ newModel ? 'Nieuw model' : 'Model ' + data.name }}</h1>
      </div>
      <div class="sticky-header-button">
        <input
          type="text"
          class="search-input"
          v-model.trim="searchTerm"
          @input="performSearch"
          placeholder="Zoek… (bijv. Omlijsting palen)"
        />
        <button type="button" class="system-button nav-button" :disabled="!searchMatches.length" @click="prevMatch" title="Vorige">
          <mdicon name="chevron-up" class="default-form-field-button-icon" size="16" />
        </button>
        <button type="button" class="system-button nav-button" :disabled="!searchMatches.length" @click="nextMatch" title="Volgende">
          <mdicon name="chevron-down" class="default-form-field-button-icon" size="16" />
        </button>
        <span class="search-counter" v-if="searchMatches.length">{{ searchIndex + 1 }} / {{ searchMatches.length }}</span>
        <LoadingButton 
          @click="newModel ? create() : update()" 
          :loading="loading" 
          :disabled="!formValid"
        >
          {{ newModel ? 'Creëren' : 'Update' }}
        </LoadingButton>
      </div>
    </div>

    <form class="default-form edit-form" @submit.prevent="">
      <div class="intro-fields">
      <div class="default-form-field">
        <label class="default-form-field-label" for="name">Naam</label>
        <input type="text" class="default-form-field-input" id="name" v-model.trim="data.name" >
      </div>

      <div class="default-form-field">
        <label class="default-form-field-label" for="type">Type</label>
        <select name="type" id="type" v-model="data.type">
          <option v-for="(type, i) in types" :key="i" :value="type">{{ type.name }}</option>
        </select>
      </div>

      <div class="default-form-field">
        <label class="default-form-field-label" for="infobox">Infobox</label>
        <select name="infobox" id="infobox" v-model="data.infobox">
          <option v-for="(infobox, i) in infoboxes" :key="i" :value="infobox">{{ `Nr.${infobox.uiIndex} ${infobox.name}` }}</option>
        </select>
      </div>

      <div class="default-form-field default-form-field--nested">
        <label class="default-form-field-label" for="trueOnlineInfobox-checkbox">Infobox Online</label>
        <div class="inline-checkbox">
          <input class="checkbox" type="checkbox" :id="`trueOnlineInfobox-checkbox`" name="trueOnlineInfobox-checkbox" v-model="data.infoboxIsOnline">
          <label :for="`trueOnlineInfobox-checkbox`" class="checkbox-label">
            <mdicon name="check-bold" class="checkbox-label-icon" :class="data.infoboxIsOnline ? 'checkbox-label-icon-active' : ''" size="15" />
          </label>
        </div>
      </div>

      <div class="default-form-field">
        <label class="default-form-field-label" for="category">Categorie</label>
        <select name="type" id="category" v-model="data.category">
          <option v-for="(category, i) in categories" :key="i" :value="category">{{ category.name }}</option>
        </select>
      </div>

      <div class="default-form-field">
        <label class="default-form-field-label" for="trueOnline">Online</label>
        <div class="inline-checkbox-group">
          <div class="inline-checkbox">
            <input class="checkbox" type="checkbox" :id="`trueOnline-checkbox`" name="trueOnline-checkbox" v-model="data.online">
            <label :for="`trueOnline-checkbox`" class="checkbox-label">
              <mdicon name="check-bold" class="checkbox-label-icon" :class="data.online ? 'checkbox-label-icon-active' : ''" size="15" />
            </label>
          </div>
          <span class="checkbox-help-text">(Model zichtbaar in configurator)</span>
        </div>
      </div>

      <div class="default-form-field">
        <label class="default-form-field-label" for="maatwerk">Maatwerk</label>
        <input class="checkbox" type="checkbox" :id="`maatwerk-checkbox`" name="maatwerk-checkbox" v-model="data.maatwerk.enabled">
        <label :for="`maatwerk-checkbox`" class="checkbox-label">
          <mdicon name="check-bold" class="checkbox-label-icon" :class="data.maatwerk.enabled ? 'checkbox-label-icon-active' : ''" size="15" />
        </label>
      </div>
      <template v-if="data.maatwerk.enabled">
        <div class="default-form-field default-form-field--nested">
          <label class="default-form-field-label" for="maatwerkWidthMin">Minimale breedte</label>
          <input type="number" class="default-form-field-input" id="maatwerkWidthMin" v-model.number="data.maatwerk.width.min">
        </div>

        <div class="default-form-field default-form-field--nested">
          <label class="default-form-field-label" for="maatwerkWidthMax">Maximale breedte</label>
          <input type="number" class="default-form-field-input" id="maatwerkWidthMax" v-model.number="data.maatwerk.width.max">
        </div>

        <div class="default-form-field default-form-field--nested">
          <label class="default-form-field-label" for="maatwerkDepthMin">Minimale diepte</label>
          <input type="number" class="default-form-field-input" id="maatwerkDepthMin" v-model.number="data.maatwerk.depth.min">
        </div>

        <div class="default-form-field default-form-field--nested">
          <label class="default-form-field-label" for="maatwerkDepthMax">Maximale diepte</label>
          <input type="number" class="default-form-field-input" id="maatwerkDepthMax" v-model.number="data.maatwerk.depth.max">
        </div>
      </template>

      <div class="default-form-field standaardmaten-field">
        <label class="default-form-field-label" for="standaardmatenDisabled">Standaard maten uit</label>
        <div class="standaardmaten-inline">
          <input class="checkbox" type="checkbox" id="standaardmatenDisabled" name="standaardmatenDisabled-checkbox" v-model="data.standaardmatenDisabled">
          <label for="standaardmatenDisabled" class="checkbox-label">
            <mdicon name="check-bold" class="checkbox-label-icon" :class="data.standaardmatenDisabled ? 'checkbox-label-icon-active' : ''" size="15" />
          </label>
          <span class="standaardmaten-help">(Vul nog wel minimaal één diepte, en één breedte, stap volledig in)</span>
        </div>
      </div>
      <div class="default-form-field">
        <label class="default-form-field-label" for="minWallWidth">Minimale muur breedte</label>
        <input type="number" class="default-form-field-input" id="minWallWidth" v-model.number="data.wall.minWallWidth">
      </div>

      <div class="default-form-field default-form-field--nested">
        <label class="default-form-field-label" for="needsSpaceForStaander">Heeft ruimte nodig voor staander</label>
        <div class="inline-checkbox">
          <input class="checkbox" type="checkbox" id="needsSpaceForStaander" name="needsSpaceForStaander-checkbox" v-model="data.wall.needsSpaceForStaander">
          <label for="needsSpaceForStaander" class="checkbox-label">
            <mdicon name="check-bold" class="checkbox-label-icon" :class="data.wall.needsSpaceForStaander ? 'checkbox-label-icon-active' : ''" size="15" />
          </label>
        </div>
      </div>
      </div>

      <!-- Wall/roof selectors remain stacked -->
      <div class="sizes sizes-roof">
        <div class="roof-grid">
          <label class="default-form-field-label roof-label">Dakafwerkingen:</label>
          <div class="roof-list">
            <div v-for="(roofFinish, i) in data.roofFinishes" :key="i" class="rooffinish-item">
              <label class="default-form-field-input" :class="textures.filter(t => t.id == roofFinish)[0] ? '' : 'error'">{{textures.filter(t => t.id == roofFinish)[0] ? textures.filter(t => t.id == roofFinish)[0].name : 'ERROR: Texture not Found.'}} {{textures.filter(t => t.id == roofFinish)[0] ? colors.filter(c => c.id == textures.filter(t => t.id == roofFinish)[0].color)[0].name : ''}}</label>
              <button class="default-form-field-button" @mousedown="removeRoofFinish(i)">
                <mdicon name="minus" class="default-form-field-button-icon" size="18" />
              </button>
            </div>
            <div class="roof-add">
              <select ref="roofFinishSelector" class="default-form-field-input">
                <option hidden value="hidden">Selecteer een dakafwerking</option>
                <option v-for="(texture, i) in filteredTextures" :key="i" :value="texture.id">{{texture.name}} {{colors.filter(c => c.id == texture.color)[0].name}}</option>
              </select>
              <button @click="addRoofFinish()" class="default-form-field-button" title="Voeg de geselecteerde dakafwerking toe">+</button>
            </div>
          </div>
        </div>
      </div>

  <!-- Wand afwerkingen -->
      <div class="sizes">
        <label class="default-form-field-label" style="margin-bottom: 6px;">Wand afwerkingen:</label>
        <div class="default-form-field sizes-toggle">
          <label class="default-form-field-label">Gebruik korte naam in deze CMS UI</label>
          <div>
            <input class="checkbox" type="checkbox" id="userShortName" name="userShortName" v-model="userShortName">
            <label for="userShortName" class="checkbox-label" style="margin-right: 0px;">
              <mdicon name="check-bold" class="checkbox-label-icon" :class="userShortName ? 'checkbox-label-icon-active' : ''" size="15" />
            </label>
          </div>
        </div>

        <div class="wallfinishes-grid" style="display: grid; grid-template-columns: 300px 1fr; gap: 8px">

          <div class="wallfinishes-left">
            <div style="height: var(--wf-head-h, 38px)"></div>
            <div v-for="(wallFinish, i) in allWallFinishes" :key="'wallFinishName'+i" class="rooffinish-item wallfinishes-left-item">
              <label class="breedte-regel-first">{{textures.filter(t => t.id == wallFinish.id)[0].name}}</label>
            </div>
          </div>

          <div style="max-width: 100%">
            <table class="wallfinishesTable">
              <thead>
                <tr>
                  <th v-for="(wallPosition, i) in wallTypesAndsides" :key="'typeHeader'+i" style="text-align: left">
                    <span class="rotated-header">{{userShortName ? wallPosition.short : wallPosition.full}}</span>
                  </th>
                  <th class="actions-col" style="text-align: left;">Volgorde</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(wallFinish, i) in allWallFinishes" :key="'wallFinish'+i">
                  <td v-for="(wallPosition, j) in wallTypesAndsides" :key="'type'+i+'_'+j">
                    <div class="inline-checkbox">
                      <input class="checkbox" type="checkbox" :id="`wallFinishEnabled_${i}_${j}`" name="overstekKlosRound" :checked="data.wallFinishes[wallPosition.id].includes(wallFinish.id)" @change="changeWallfinish($event, wallPosition.id, wallFinish.id)">
                      <label :for="`wallFinishEnabled_${i}_${j}`" class="checkbox-label" style="margin-right: 0px">
                        <mdicon name="check-bold" class="checkbox-label-icon" :class="data.wallFinishes[wallPosition.id].includes(wallFinish.id) ? 'checkbox-label-icon-active' : ''" size="15" />
                      </label>
                    </div>
                  </td>
                  <td class="actions-cell">
                    <div class="texture-order inline-actions">
                      <button class="default-form-field-button texture-order-button" @click="moveTextureUp(i)">
                        <mdicon name="chevron-up" class="default-form-field-button-icon" size="25" />
                      </button>
                      <button class="default-form-field-button texture-order-button" @click="moveTextureDown(i)">
                        <mdicon name="chevron-down" class="default-form-field-button-icon" size="25" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

  <div class="wallfinishes-grid" style="display: grid; grid-template-columns: 300px 1fr 80px; gap: 0px" v-if="data.defaultSizes[0] && data.defaultSizes[0].wall.height">

          <div class="wallfinishes-left">
            <div style="height: var(--wf-head-h, 38px)"></div>
            <div v-for="(wallDimension, i) in wallDimensions" :key="'wallFinishName'+i" class="rooffinish-item wallfinishes-left-item">
              <label class="breedte-regel-first">{{wallDimension}}</label>
            </div>
          </div>

          <div style="max-width: 100%">
            <table class="wallfinishesTable">
              <thead>
                <tr>
                  <th v-for="(wallPosition, i) in wallTypesAndsidesforDimensions" :key="'typeHeader'+i" style="text-align: left">
                    <span class="rotated-header">{{userShortName ? wallPosition.short : wallPosition.full}}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(wallDimension, i) in wallDimensions" :key="'wallFinish'+i">
                  <td v-for="(wallPosition, j) in wallTypesAndsidesforDimensions" :key="'type'+i+'_'+j">
                    <input type="number" class="default-form-field-input" style="width:50px" v-model.number="data.defaultSizes[0].wall.height.find(height => height.position === wallPosition.id).value">
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div></div>
        </div>
      </div>



      <div class="default-form-field default-form-field--two-col">
        <label class="default-form-field-label" for="overstekKlosZ">Overstekklos afgerond</label>
        <div class="inline-checkbox">
          <input class="checkbox" type="checkbox" id="overstekKlosRound" name="overstekKlosRound" v-model="data.overstekKlosRound">
          <label for="overstekKlosRound" class="checkbox-label" style="margin-right: 0">
            <mdicon name="check-bold" class="checkbox-label-icon" :class="data.overstekKlosRound ? 'checkbox-label-icon-active' : ''" size="15" />
          </label>
        </div>
      </div>

      <div class="default-form-field">
        <label class="default-form-field-label" style="width: 100%">Opties:</label>
        <div class="optionslist">
          <div class="optionslist-item">
            <label class="default-form-field-label" for="fundering">Fundering</label>
            <div class="optionslist-check">
              <input class="checkbox" type="checkbox" id="fundering" name="fundering" v-model="data.availableOptions.fundering">
              <label for="fundering" class="checkbox-label">
                <mdicon name="check-bold" class="checkbox-label-icon optionslist-checkbox" :class="data.availableOptions.fundering ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>
            </div>
          </div>

          <div class="optionslist-item">
            <label class="default-form-field-label" for="dakgoot">Dakgoot</label>
            <div class="optionslist-check">
              <input class="checkbox" type="checkbox" id="dakgoot" name="dakgoot" v-model="data.availableOptions.dakgoot">
              <label for="dakgoot" class="checkbox-label">
                <mdicon name="check-bold" class="checkbox-label-icon optionslist-checkbox" :class="data.availableOptions.dakgoot ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>
            </div>
          </div>

          <div class="optionslist-item">
            <label class="default-form-field-label" for="lichtstraten">Lichtstraten</label>
            <div class="optionslist-check">
              <input class="checkbox" type="checkbox" id="lichtstraten" name="lichtstraten" v-model="data.availableOptions.lichtstraten">
              <label for="lichtstraten" class="checkbox-label">
                <mdicon name="check-bold" class="checkbox-label-icon optionslist-checkbox" :class="data.availableOptions.lichtstraten ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>
            </div>
          </div>

          <div class="optionslist-item">
            <label class="default-form-field-label" for="ledverlichting">LED verlichting</label>
            <div class="optionslist-check">
              <input class="checkbox" type="checkbox" id="ledverlichting" name="ledverlichting" v-model="data.availableOptions.ledverlichting">
              <label for="ledverlichting" class="checkbox-label">
                <mdicon name="check-bold" class="checkbox-label-icon optionslist-checkbox" :class="data.availableOptions.ledverlichting ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- mesh names -->
      <div class="default-form-field">
        <label class="default-form-field-label" style="width: 100%">mesh namen:</label>
        <div class="optionslist">
          <div class="optionslist-item" v-for="key in meshNameKeys" :key="key">
            <label class="default-form-field-label" :for="key">{{ key }}</label>
            <select name="type" id="category" v-model="data.meshNames[key]" style="max-width: 300px;">
              <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
            </select>
          </div>
        </div>
      </div>

      <!-- NEW: Single-row horizontally scrollable cards for each depth step -->
      <div class="depth-row-frame">
        <div class="depth-row-frame-header">
          <span class="depth-row-frame-title">Dieptestappen</span>
          <button class="default-form-field-button add-depth-button" @click="addSize()" title="Voeg diepte stap toe">
            <mdicon name="plus" class="default-form-field-button-icon" size="18" />
            <span class="btn-text">Voeg diepte stap toe</span>
          </button>
          <div class="spacer"></div>
        </div>
        <div class="depth-row-scroll">
  <div :class="['depth-card', { 'depth-card-deleted': !!size.__markedForDeletion, 'depth-card-copied': !!size.__isNewCopy }]" v-for="(size, i) in data.defaultSizes" :key="i" :data-depth-index="i">
          <div class="sizes-header">
            <span class="sizes-header-name">{{ 'Diepte stap ' + (i+1) }}</span>
            <span v-if="size.__markedForDeletion" class="depth-badge-delete" title="Wordt verwijderd na opslaan">Wordt verwijderd</span>

            <div class="spacer"></div>

            <!-- Kopieer -->
            <button
              type="button"
              class="system-button"
              title="Kopieer diepte stap"
              @click.prevent="copyDepth(i)"
            >
              <mdicon name="content-copy" class="default-form-field-button-icon" size="16" />
              <span class="btn-text">Kopieer</span>
            </button>

            <!-- Verwijderen / Annuleren (rood) -->
            <button
              v-if="!size.__markedForDeletion"
              class="default-form-field-button danger remove-depth-button"
              @click.prevent="toggleRemoveDepth(i)"
            >
              <mdicon name="delete" class="default-form-field-button-icon" size="14" />
              <span class="btn-text">Verwijder</span>
            </button>
            <button
              v-else
              type="button"
              class="cancel-depth-button"
              @click.prevent="toggleRemoveDepth(i)"
              title="Maak verwijderen ongedaan"
            >
              <mdicon name="backup-restore" class="default-form-field-button-icon" size="16" />
              <span class="btn-text">Annuleren</span>
            </button>

            <!-- Annuleer kopie (oranje) -->
            <button
              v-if="size.__isNewCopy && !size.__markedForDeletion"
              type="button"
              class="cancel-copy-button"
              @click.prevent="cancelCopiedDepth(i)"
              title="Kopie ongedaan maken"
            >
              <mdicon name="backup-restore" class="default-form-field-button-icon" size="16" />
              <span class="btn-text">Annuleren</span>
            </button>

            <span class="sizes-form-toggle-help">Actief in model</span>
            <input class="checkbox" type="checkbox" :id="`diepte-enabled-checkbox_${i}`" name="trim-checkbox" v-model="size.enabled">
            <label :for="`diepte-enabled-checkbox_${i}`" class="checkbox-label sizes-form-checkbox" style="margin-left: 10px">
              <mdicon name="check-bold" class="checkbox-label-icon" :class="size.enabled ? 'checkbox-label-icon-active' : ''" size="15" />
            </label>

            <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('diepte', i)">
                <mdicon :name="groupshown[i].diepte ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
            </button>
          </div>

          <div v-if="groupshown[i].diepte">
            <!-- Reuse the exact same content and bindings from Model.vue -->
            <!-- Afmetingen -->
            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Afmetingen:</h1>
              <div class="spacer"></div>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('afmetingen', i)">
                <mdicon :name="groupshown[i].afmetingen ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].afmetingen ? '' : 'sizes-form-collapsed'">
              <label class="default-form-field-label" for="diepte">Diepte</label>
              <input
                type="number"
                class="default-form-field-input BottomM"
                id="diepte"
                v-model.number="size.diepte"
                @change="onDepthChange(i)"
              >

              <label class="default-form-field-label" for="hoogtePaal">Hoogte paal</label>
              <input type="number" class="default-form-field-input BottomM" id="hoogtePaal" v-model.trim="size.hoogtePaal" >

              <label class="default-form-field-label" for="maximaleMuurBreedte">Maximale muur breedte</label>
              <input type="number" class="default-form-field-input BottomM" id="maximaleMuurBreedte" v-model.trim="size.maximaleMuurBreedte">

              <label class="default-form-field-label" for="staanderDikte">Staander dikte</label>
              <input type="number" class="default-form-field-input BottomM" id="staanderDikte" v-model.trim="size.staanderDikte" >

              <label class="default-form-field-label" for="poerDikte">Poer dikte</label>
              <input type="number" class="default-form-field-input BottomM" id="poerDikte" v-model.trim="size.poer.z" >

              <label class="default-form-field-label" for="poerLength">Poer lengte</label>
              <input type="number" class="default-form-field-input BottomM" id="poerLength" v-model.trim="size.poer.y" >
            </div>

            <!-- BEGIN: Full original sections from Model.vue (UI unchanged) -->

            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Schoor:</h1>
              <div class="spacer"></div>
              <span class="sizes-form-toggle-help">Actief in model</span>
              <input class="checkbox" type="checkbox" :id="`schoor-checkbox`+i" name="schoor-checkbox" v-model="size.schoor.enabled">
              <label :for="`schoor-checkbox`+i" class="checkbox-label sizes-form-checkbox">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.schoor.enabled ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('schoor', i)">
                <mdicon :name="groupshown[i].schoor ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].schoor ? '' : 'sizes-form-collapsed'">
              <label class="default-form-field-label" for="schoorMeshName">Schoor mesh naam</label>
              <select class="default-form-field-input" v-model="size.schoor.meshName" id="schoorMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="schoorSz">Schoor breedte</label>
              <input type="number" class="default-form-field-input" id="schoorSz" v-model.trim="size.schoor.sz" >

              <label class="default-form-field-label" for="schoorSy">Schoor hoogte</label>
              <input type="number" class="default-form-field-input" id="schoorSy" v-model.trim="size.schoor.sy" >

              <label class="default-form-field-label" for="schoorSx">Schoor dikte</label>
              <input type="number" class="default-form-field-input" id="schoorSx" v-model.trim="size.schoor.sx" >

              <label class="default-form-field-label" for="schoorSox">Schoor gat</label>
              <input type="number" class="default-form-field-input BottomM" id="schoorSox" v-model.trim="size.schoor.sox">

              <label class="default-form-field-label" for="schoorSox">Minimale muur breedte</label>
              <input type="number" class="default-form-field-input BottomM" id="schoorSox" v-model.trim="size.schoor.wallMinWidth">
            </div>


            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Ringbalk:</h1>
              <div class="spacer"></div>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('ringBalk', i)">
                <mdicon :name="groupshown[i].ringBalk ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].ringBalk ? '' : 'sizes-form-collapsed'">
              <label class="default-form-field-label" for="ringBalkMeshName">Ringbalk mesh naam</label>
              <select class="default-form-field-input" v-model="size.ringBalk.meshName" id="ringBalkMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="ringBalkY">Ringbalk hoogte</label>
              <input type="number" class="default-form-field-input" id="ringBalkY" v-model.trim="size.ringBalk.y">

              <label class="default-form-field-label" for="ringBalkZ">Ringbalk breedte</label>
              <input type="number" class="default-form-field-input BottomM" id="ringBalkZ" v-model.trim="size.ringBalk.z">
            </div>


            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Extra ringbalk:</h1>
              <div class="spacer"></div>
              <span class="sizes-form-toggle-help">Actief in model</span>
              <input class="checkbox" type="checkbox" :id="`extraRingBalk-checkbox`+i" name="extraRingBalk-checkbox" v-model="size.extraRingBalk.enabled">
              <label :for="`extraRingBalk-checkbox`+i" class="checkbox-label sizes-form-checkbox">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.extraRingBalk.enabled ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('extraRingBalk', i)">
                <mdicon :name="groupshown[i].extraRingBalk ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].extraRingBalk ? '' : 'sizes-form-collapsed'">
              <label class="default-form-field-label" for="extraRingBalkMeshName">Extra ringbalk mesh naam</label>
              <select class="default-form-field-input" v-model="size.extraRingBalk.meshName" id="extraRingBalkMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="gordingVorm" style="margin-right:10px">Gebruik gording vorm</label>
              <input class="checkbox" type="checkbox" :id="`gordingVorm`+i" name="boeideel-checkbox" v-model="size.extraRingBalk.gordingVorm">
              <label :for="`gordingVorm`+i" class="checkbox-label sizes-form-checkbox" style="float: none; display: inline-flex; margin-right:40px">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.extraRingBalk.gordingVorm ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>

              <label class="default-form-field-label" for="ringBalkY">Extra ringbalk hoogte</label>
              <input type="number" class="default-form-field-input" id="ringBalkY" v-model.trim="size.extraRingBalk.y">

              <label class="default-form-field-label" for="ringBalkZ">Extra ringbalk breedte</label>
              <input type="number" class="default-form-field-input BottomM" id="ringBalkZ" v-model.trim="size.extraRingBalk.z">

              <label class="default-form-field-label" for="lijstPosition">Positie</label>
              <select class="default-form-field-input" @change="size.extraRingBalk.position = $event.target.value">
                <option :selected="!size.extraRingBalk.position" :value="false">Buiten</option>
                <option :selected="size.extraRingBalk.position" :value="true">Binnen</option>
              </select>
            </div>

            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Ringbalk tussen paal:</h1>
              <div class="spacer"></div>
              <span class="sizes-form-toggle-help">Actief in model</span>
              <input class="checkbox" type="checkbox" :id="`ringbalkTussenPaal-checkbox`+i" name="ringbalkTussenPaal-checkbox" v-model="size.ringbalkTussenPaal.enabled">
              <label :for="`ringbalkTussenPaal-checkbox`+i" class="checkbox-label sizes-form-checkbox">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.ringbalkTussenPaal.enabled ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('ringbalkTussenPaal', i)">
                <mdicon :name="groupshown[i].ringbalkTussenPaal ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].ringbalkTussenPaal ? '' : 'sizes-form-collapsed'">
              <label class="default-form-field-label" for="ringbalkTussenPaalMeshName">Ringbalk tussen paal mesh naam</label>
              <select class="default-form-field-input" v-model="size.ringbalkTussenPaal.meshName" id="ringbalkTussenPaalMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="ringBalkY">Ringbalk tussen paal hoogte</label>
              <input type="number" class="default-form-field-input" id="ringBalkY" v-model.trim="size.ringbalkTussenPaal.y">

              <label class="default-form-field-label" for="ringBalkZ">Ringbalk tussen paal breedte</label>
              <input type="number" class="default-form-field-input BottomM" id="ringBalkZ" v-model.trim="size.ringbalkTussenPaal.z">
            </div>


            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Gordingen:</h1>
              <div class="spacer"></div>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('gording', i)">
                <mdicon :name="groupshown[i].gording ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].gording ? '' : 'sizes-form-collapsed'">
              <label class="default-form-field-label" for="gordingBalkMeshName">Gordingbalk mesh naam</label>
              <select class="default-form-field-input" v-model="size.gordingBalk.meshName" id="gordingBalkMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="gordingBalkY">Gordingbalk hoogte</label>
              <input type="number" class="default-form-field-input" id="gordingBalkY" v-model.trim="size.gordingBalk.y">

              <label class="default-form-field-label" for="gordingBalkZ">Gordingbalk breedte</label>
              <input type="number" class="default-form-field-input BottomM" id="gordingBalkZ" v-model.trim="size.gordingBalk.z">

              <label class="default-form-field-label" for="afstandTussenGordingen">Afstand tussen gordingen</label>
              <input type="number" class="default-form-field-input BottomM" id="afstandTussenGordingen" v-model.trim="size.gordingBalk.afstandTussenGordingen">

              <label class="default-form-field-label" for="pos">Verticale positionering</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.gordingBalk.position">

              <label class="default-form-field-label" for="pos">Offset x-as</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.gordingBalk.ox">

              <label class="default-form-field-label" for="pos">Offset y-as</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.gordingBalk.oy">

              <label class="default-form-field-label" for="pos">Verticale lengte</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.gordingBalk.oyl">
            </div>


            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Extra balk op gording:</h1>
              <div class="spacer"></div>
              <span class="sizes-form-toggle-help">Actief in model</span>
              <input class="checkbox" type="checkbox" :id="`extraBalkOpGording-checkbox`+i" name="extraBalkOpGording-checkbox" v-model="size.extraBalkOpGording.enabled">
              <label :for="`extraBalkOpGording-checkbox`+i" class="checkbox-label sizes-form-checkbox">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.extraBalkOpGording.enabled ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('extraBalkOpGording', i)">
                <mdicon :name="groupshown[i].extraBalkOpGording ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].extraBalkOpGording ? '' : 'sizes-form-collapsed'">
              <label class="default-form-field-label" for="extraBalkOpGordingMeshName">Extra balk op gording mesh naam</label>
              <select class="default-form-field-input" v-model="size.extraBalkOpGording.meshName" id="extraBalkOpGordingMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="ringBalkY">Extra balk op gording hoogte</label>
              <input type="number" class="default-form-field-input" id="ringBalkY" v-model.trim="size.extraBalkOpGording.y">

              <label class="default-form-field-label" for="ringBalkZ">Extra balk op gording dikte</label>
              <input type="number" class="default-form-field-input BottomM" id="ringBalkZ" v-model.trim="size.extraBalkOpGording.z">
            </div>


            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Extra plank achter:</h1>
              <div class="spacer"></div>
              <span class="sizes-form-toggle-help">Actief in model</span>
              <input class="checkbox" type="checkbox" :id="`extraPlankAchter-checkbox`+i" name="extraPlankAchter-checkbox" v-model="size.extraPlankAchter.enabled">
              <label :for="`extraPlankAchter-checkbox`+i" class="checkbox-label sizes-form-checkbox">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.extraPlankAchter.enabled ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('extraPlankAchter', i)">
                <mdicon :name="groupshown[i].extraPlankAchter ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].extraPlankAchter ? '' : 'sizes-form-collapsed'">
              <label class="default-form-field-label" for="extraPlankAchterMeshName">Extra plank achter mesh naam</label>
              <select class="default-form-field-input" v-model="size.extraPlankAchter.meshName" id="extraPlankAchterMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="hoogte">Hoogte</label>
              <input type="number" class="default-form-field-input" id="hoogte" v-model.trim="size.extraPlankAchter.y">

              <label class="default-form-field-label" for="dikte">Dikte</label>
              <input type="number" class="default-form-field-input BottomM" id="dikte" v-model.trim="size.extraPlankAchter.z">

              <label class="default-form-field-label" for="xBov">Klos stuk lengte boven</label>
              <input type="number" class="default-form-field-input BottomM" id="xBov" v-model.trim="size.extraPlankAchter.xBov">

              <label class="default-form-field-label" for="xOnd">Klos stuk lengte onder</label>
              <input type="number" class="default-form-field-input BottomM" id="xOnd" v-model.trim="size.extraPlankAchter.xOnd">

              <label class="default-form-field-label" for="yKop">Klos stuk hoogte kop</label>
              <input type="number" class="default-form-field-input BottomM" id="yKop" v-model.trim="size.extraPlankAchter.yKop">
            </div>


            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Ringbalk gording:</h1>
              <div class="spacer"></div>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('ringBalkGording', i)">
                <mdicon :name="groupshown[i].ringBalkGording ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].ringBalkGording ? '' : 'sizes-form-collapsed'">
              <label class="default-form-field-label" for="ringBalkGordingMeshName">Ringbalk gording mesh naam</label>
              <select class="default-form-field-input" v-model="size.ringBalkGording.meshName" id="ringBalkGordingMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="gordingBalkY">Ringbalk gording hoogte</label>
              <input type="number" class="default-form-field-input" id="gordingBalkY" v-model.trim="size.ringBalkGording.y">

              <label class="default-form-field-label" for="gordingBalkZ">Ringbalk gording breedte</label>
              <input type="number" class="default-form-field-input BottomM" id="gordingBalkZ" v-model.trim="size.ringBalkGording.z">

              <label class="default-form-field-label" for="gordingBalkPosition">Verticale positionering</label>
              <input type="number" class="default-form-field-input" id="gordingBalkPosition" v-model.trim="size.ringBalkGording.position">
            </div>


            
            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Overstekklos:</h1>
              <div class="spacer"></div>
              <span class="sizes-form-toggle-help">Actief in model</span>
              <input class="checkbox" type="checkbox" :id="`overstekKlos-checkbox`+i" name="overstekKlos-checkbox" v-model="size.overstekKlos.enabled">
              <label :for="`overstekKlos-checkbox`+i" class="checkbox-label sizes-form-checkbox">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.overstekKlos.enabled ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('overstekKlos', i)">
                <mdicon :name="groupshown[i].overstekKlos ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].overstekKlos ? '' : 'sizes-form-collapsed'">
              <label class="default-form-field-label" for="overstekKlosMeshName">Overstekklos mesh naam</label>
              <select class="default-form-field-input" v-model="size.overstekKlos.meshName" id="overstekKlosMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="zDik">Overstekklos dikte tussen</label>
              <input type="number" class="default-form-field-input" id="zDik" v-model.trim="size.overstekKlos.zDik">

              <label class="default-form-field-label" for="zDikPaal">Overstekklos dikte paal</label>
              <input type="number" class="default-form-field-input" id="zDikPaal" v-model.trim="size.overstekKlos.zDikPaal">


              
              <label class="default-form-field-label" for="klosVoEnabled" style="margin-right:10px">Klos Voor Aan</label>
                <input class="checkbox" type="checkbox" :id="`klosVoEnabled`+i" name="boeideel-checkbox" v-model="size.overstekKlos.klosVoEnabled">
                <label :for="`klosVoEnabled`+i" class="checkbox-label sizes-form-checkbox" style="float: none; display: inline-flex; margin-right:40px">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.overstekKlos.klosVoEnabled ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>

              <label class="default-form-field-label" for="klosVoEditable" style="margin-right:10px">Klos Voor uitzetbaar</label>
              <input class="checkbox" type="checkbox" :id="`klosVoEditable`+i" name="boeideel-checkbox" v-model="size.overstekKlos.klosVoEditable">
              <label :for="`klosVoEditable`+i" class="checkbox-label sizes-form-checkbox" style="float: none; display: inline-flex; margin-right:40px">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.overstekKlos.klosVoEditable ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>

              <label class="default-form-field-label" for="xBovVo">Klos Voor Lengte Boven</label>
              <input type="number" class="default-form-field-input" id="xBovVo" v-model.trim="size.overstekKlos.xBovVo">

              <label class="default-form-field-label" for="xOndVo">Klos Voor Lengte Onder</label>
              <input type="number" class="default-form-field-input" id="xOndVo" v-model.trim="size.overstekKlos.xOndVo">

              <label class="default-form-field-label" for="yHooVo">Klos Voor Hoogte</label>
              <input type="number" class="default-form-field-input" id="yHooVo" v-model.trim="size.overstekKlos.yHooVo">

              <label class="default-form-field-label" for="yKopVo">Klos Voor Hoogte Kopkant</label>
              <input type="number" class="default-form-field-input" id="yKopVo" v-model.trim="size.overstekKlos.yKopVo">



              <label class="default-form-field-label" for="klosAcEnabled" style="margin-right:10px">Klos Achter Aan</label>
                <input class="checkbox" type="checkbox" :id="`klosAcEnabled`+i" name="boeideel-checkbox" v-model="size.overstekKlos.klosAcEnabled">
                <label :for="`klosAcEnabled`+i" class="checkbox-label sizes-form-checkbox" style="float: none; display: inline-flex; margin-right:40px">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.overstekKlos.klosAcEnabled ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>

              <label class="default-form-field-label" for="klosAcEditable" style="margin-right:10px">Klos Achter uitzetbaar</label>
              <input class="checkbox" type="checkbox" :id="`klosAcEditable`+i" name="boeideel-checkbox" v-model="size.overstekKlos.klosAcEditable">
              <label :for="`klosAcEditable`+i" class="checkbox-label sizes-form-checkbox" style="float: none; display: inline-flex; margin-right:40px">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.overstekKlos.klosAcEditable ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>

              <label class="default-form-field-label" for="xBovAc">Klos Achter Lengte Boven</label>
              <input type="number" class="default-form-field-input" id="xBovAc" v-model.trim="size.overstekKlos.xBovAc">

              <label class="default-form-field-label" for="xOndAc">Klos Achter Lengte Onder</label>
              <input type="number" class="default-form-field-input" id="xOndAc" v-model.trim="size.overstekKlos.xOndAc">

              <label class="default-form-field-label" for="yHooAc">Klos Achter Hoogte</label>
              <input type="number" class="default-form-field-input" id="yHooAc" v-model.trim="size.overstekKlos.yHooAc">

              <label class="default-form-field-label" for="yKopAc">Klos Achter Hoogte Kopkant</label>
              <input type="number" class="default-form-field-input" id="yKopAc" v-model.trim="size.overstekKlos.yKopAc">



              <label class="default-form-field-label" for="klosLiEnabled" style="margin-right:10px">Klos Links Aan</label>
                <input class="checkbox" type="checkbox" :id="`klosLiEnabled`+i" name="boeideel-checkbox" v-model="size.overstekKlos.klosLiEnabled">
                <label :for="`klosLiEnabled`+i" class="checkbox-label sizes-form-checkbox" style="float: none; display: inline-flex; margin-right:40px">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.overstekKlos.klosLiEnabled ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>

              <label class="default-form-field-label" for="klosLiEditable" style="margin-right:10px">Klos Links uitzetbaar</label>
              <input class="checkbox" type="checkbox" :id="`klosLiEditable`+i" name="boeideel-checkbox" v-model="size.overstekKlos.klosLiEditable">
              <label :for="`klosLiEditable`+i" class="checkbox-label sizes-form-checkbox" style="float: none; display: inline-flex; margin-right:40px">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.overstekKlos.klosLiEditable ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>

              <label class="default-form-field-label" for="xBovLi">Klos Links Lengte Boven</label>
              <input type="number" class="default-form-field-input" id="xBovLi" v-model.trim="size.overstekKlos.xBovLi">

              <label class="default-form-field-label" for="xOndLi">Klos Links Lengte Onder</label>
              <input type="number" class="default-form-field-input" id="xOndLi" v-model.trim="size.overstekKlos.xOndLi">

              <label class="default-form-field-label" for="yHooLi">Klos Links Hoogte</label>
              <input type="number" class="default-form-field-input" id="yHooLi" v-model.trim="size.overstekKlos.yHooLi">

              <label class="default-form-field-label" for="yKopLi">Klos Links Hoogte Kopkant</label>
              <input type="number" class="default-form-field-input" id="yKopLi" v-model.trim="size.overstekKlos.yKopLi">



              <label class="default-form-field-label" for="klosReEnabled" style="margin-right:10px">Klos Rechts Aan</label>
                <input class="checkbox" type="checkbox" :id="`klosReEnabled`+i" name="boeideel-checkbox" v-model="size.overstekKlos.klosReEnabled">
                <label :for="`klosReEnabled`+i" class="checkbox-label sizes-form-checkbox" style="float: none; display: inline-flex; margin-right:40px">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.overstekKlos.klosReEnabled ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>

              <label class="default-form-field-label" for="klosReEditable" style="margin-right:10px">Klos Rechts uitzetbaar</label>
              <input class="checkbox" type="checkbox" :id="`klosReEditable`+i" name="boeideel-checkbox" v-model="size.overstekKlos.klosReEditable">
              <label :for="`klosReEditable`+i" class="checkbox-label sizes-form-checkbox" style="float: none; display: inline-flex; margin-right:40px">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.overstekKlos.klosReEditable ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>

              <label class="default-form-field-label" for="xBovRe">Klos Rechts Lengte Boven</label>
              <input type="number" class="default-form-field-input" id="xBovRe" v-model.trim="size.overstekKlos.xBovRe">

              <label class="default-form-field-label" for="xOndRe">Klos Rechts Lengte Onder</label>
              <input type="number" class="default-form-field-input" id="xOndRe" v-model.trim="size.overstekKlos.xOndRe">

              <label class="default-form-field-label" for="yHooRe">Klos Rechts Hoogte</label>
              <input type="number" class="default-form-field-input" id="yHooRe" v-model.trim="size.overstekKlos.yHooRe">

              <label class="default-form-field-label" for="yKopRe">Klos Rechts Hoogte Kopkant</label>
              <input type="number" class="default-form-field-input" id="yKopRe" v-model.trim="size.overstekKlos.yKopRe">

              <label class="default-form-field-label" for="outsideMarginPraag">Buiten marge praag</label>
              <input type="number" class="default-form-field-input" id="outsideMarginPraag" v-model.trim="size.overstekKlos.outsideMarginPraag">

              <label class="default-form-field-label" for="losseKopMaxHartAfstand">Losse kop max hart afstand</label>
              <input type="number" class="default-form-field-input" id="losseKopMaxHartAfstand" v-model.trim="size.overstekKlos.losseKopMaxHartAfstand">

            </div>


            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Boeideel:</h1>
              <div class="spacer"></div>
              <span class="sizes-form-toggle-help">Actief in model</span>
              <input class="checkbox" type="checkbox" :id="`boeideel-checkbox`+i" name="boeideel-checkbox" v-model="size.boeideel.enabled">
              <label :for="`boeideel-checkbox`+i" class="checkbox-label sizes-form-checkbox">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.boeideel.enabled ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('boeideel', i)">
                <mdicon :name="groupshown[i].boeideel ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].boeideel ? '' : 'sizes-form-collapsed'">
              <label class="default-form-field-label" for="boeideelPlankMeshName">Boeideel mesh naam</label>
              <select class="default-form-field-input" v-model="size.boeideel.boeideelPlankMeshName" id="boeideelPlankMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="boeideelLijstMeshName">Boeideel lijst mesh naam</label>
              <select class="default-form-field-input" v-model="size.boeideel.boeideelLijstMeshName" id="boeideelLijstMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="dekplankMeshName">Dekplank mesh naam</label>
              <select class="default-form-field-input" v-model="size.boeideel.dekplankMeshName" id="dekplankMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="douglasPlankenMeshName">Douglas planken mesh naam</label>
              <select class="default-form-field-input" v-model="size.boeideel.douglasPlankenMeshName" id="douglasPlankenMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="boeideelY">Boeideel hoogte</label>
              <input type="number" class="default-form-field-input" id="boeideelY" v-model.trim="size.boeideel.y">

              <label class="default-form-field-label" for="boeideelZ">Boeideel dikte</label>
              <input type="number" class="default-form-field-input BottomM" id="boeideelZ" v-model.trim="size.boeideel.z">

              <label class="default-form-field-label" for="byl">boeideel hoogte verticaal zaagstuk voorzijde</label>
              <input type="number" class="default-form-field-input BottomM" id="byl" v-model.trim="size.boeideel.byl">

              <label class="default-form-field-label" for="bylAchter">boeideel hoogte verticaal zaagstuk achterzijde</label>
              <input type="number" class="default-form-field-input BottomM" id="bylAchter" v-model.trim="size.boeideel.bylAchter">

              <label class="default-form-field-label" for="horizontaleOffset">binnenkant boeideel horizontale offset t.o.v. het frame</label>
              <input type="number" class="default-form-field-input BottomM" id="horizontaleOffset" v-model.trim="size.boeideel.horizontaleOffset">

              <label class="default-form-field-label" for="pos">Boeideel positionering</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.boeideel.position">

              <label class="default-form-field-label" for="boeilijst">Boeilijst aan</label>
              <input class="checkbox" type="checkbox" :id="`boeilijst-checkbox`+i" name="boeideel-checkbox" v-model="size.boeideel.lijstEnabled">
              <label :for="`boeilijst-checkbox`+i" class="checkbox-label sizes-form-checkbox">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.boeideel.lijstEnabled ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>

              <label class="default-form-field-label" for="boeilijstY">Boeilijst hoogte</label>
              <input type="number" class="default-form-field-input" id="boeilijstY" v-model.trim="size.boeideel.ly">

              <label class="default-form-field-label" for="boeilijstZ">Boeilijst dikte</label>
              <input type="number" class="default-form-field-input BottomM" id="boeilijstZ" v-model.trim="size.boeideel.lz">

              <label class="default-form-field-label" for="lijstPosition">Boeilijst positie</label>
              <select class="default-form-field-input" @change="changeBoeilijstPosition(i, $event.target.value)">
                <option :selected="!size.lijstPosition" :value="false">Buiten</option>
                <option :selected="size.lijstPosition" :value="true">Binnen</option>
              </select>

              <label class="default-form-field-label" for="dpz">Dekplank breedte</label>
              <input type="number" class="default-form-field-input" id="dpz" v-model.trim="size.boeideel.dpz">

              <label class="default-form-field-label" for="dpy">Dekplank dikte</label>
              <input type="number" class="default-form-field-input BottomM" id="dpy" v-model.trim="size.boeideel.dpy">

              <label class="default-form-field-label" for="dpOffsetKopkant">Dekplank offset kopkant</label>
              <input type="number" class="default-form-field-input BottomM" id="dpOffsetKopkant" v-model.trim="size.boeideel.dpOffsetKopkant">

              <label class="default-form-field-label" for="dpOffsetGootzijde">Dekplank offset gootzijde</label>
              <input type="number" class="default-form-field-input BottomM" id="dpOffsetGootzijde" v-model.trim="size.boeideel.dpOffsetGootzijde">

              <label class="default-form-field-label" for="douglasPlanken">Douglas Planken</label>
              <input class="checkbox" type="checkbox" :id="`douglasPlanken-checkbox`+i" name="boeideel-checkbox" v-model="size.boeideel.douglasPlanken">
              <label :for="`douglasPlanken-checkbox`+i" class="checkbox-label sizes-form-checkbox">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.boeideel.douglasPlanken ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>
            </div>


            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Trim:</h1>
              <div class="spacer"></div>
              <span class="sizes-form-toggle-help">Actief in model</span>
              <input class="checkbox" type="checkbox" :id="`trim-checkbox`+i" name="trim-checkbox" v-model="size.trim.enabled">
              <label :for="`trim-checkbox`+i" class="checkbox-label sizes-form-checkbox">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.trim.enabled ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('trim', i)">
                <mdicon :name="groupshown[i].trim ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].trim ? '' : 'sizes-form-collapsed'">
              <label class="default-form-field-label" for="trimMeshName">Trim mesh naam</label>
              <select class="default-form-field-input" v-model="size.trim.meshName" id="trimMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="pos">Hoogte</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.trim.y">

              <label class="default-form-field-label" for="pos">Dikte</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.trim.z">

              <label class="default-form-field-label" for="pos">overhang</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.trim.overhang">
            </div>
            <div>
              <div class="sizes-form" :class="groupshown[i].trim ? '' : 'sizes-form-collapsed'">
                <label class="default-form-field-label" for="pos">kleur</label>
                <div>
                    <div class="sizes-form" style="grid-template-columns: 80px 1fr 32px; margin-left: 0px;" v-for="(color, c) in size.trim.color" :key="c">
                    <div class="default-form-field-input trimColorBlock" :style="`background-image: url('${previewsServer}/textures/${textures.filter(textures => textures.id === color)[0] ? textures.filter(textures => textures.id === color)[0].url : ''}')`"></div>
                      <label class="default-form-field-input" style="margin-left:10px">{{textures.filter(colors => colors.id === color)[0] ? textures.filter(textures => textures.id === color)[0].name : ''}}</label>
                      <button class="default-form-field-button" @mousedown="removeTrimColor(i,c)">
                        <mdicon name="minus" class="default-form-field-button-icon" size="18" />
                      </button>
                    </div>
                    <div class="sizes-form" style="grid-template-columns: 1fr 32px; margin-left: 0px; margin-bottom:0px;">
                    <select :ref="'trimColorSelector'+i" class="default-form-field-input">
                      <option hidden value="hidden">Selecteer een kleur</option>
                      <option v-for="(color, j) in textures" :key="j" :value="color.id">{{color.name}}</option>
                    </select>
                    <button class="default-form-field-button" @mousedown="addTrimColor(i)">
                      <mdicon name="plus" class="default-form-field-button-icon" size="18" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="sizes-form" :class="groupshown[i].trim ? '' : 'sizes-form-collapsed'">
              <label class="default-form-field-label" for="pos">Positie</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.trim.position">
            </div>


            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Dak:</h1>
              <div class="spacer"></div>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('dak', i)">
                <mdicon :name="groupshown[i].dak ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].dak ? '' : 'sizes-form-collapsed'">  
              <label class="default-form-field-label" for="dakbeschotMeshName">Dakbeschot mesh naam</label>
              <select class="default-form-field-input" v-model="size.dak.dakbeschotMeshName" id="dakbeschotMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="dakbedekkingMeshName">Dakbedekking mesh naam</label>
              <select class="default-form-field-input" v-model="size.dak.dakbedekkingMeshName" id="dakbedekkingMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>
                    
              <label class="default-form-field-label" for="pos">Dakbeschot offset</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.dak.offset">
      
              <label class="default-form-field-label" for="pos">Dakbeschot overhang</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.dak.overhang">
        
              <label class="default-form-field-label" for="pos">Dakbeschot dikte</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.dak.beschotDikte">
         
              <label class="default-form-field-label" for="pos">Dakhelling</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.dak.helling">
         
              <label class="default-form-field-label" for="hellingAchter">Dakhelling achter</label>
              <input type="number" class="default-form-field-input" id="hellingAchter" v-model.trim="size.dak.hellingAchter">
        
              <label class="default-form-field-label" for="pos">Dakpan overhang lengte voorkant</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.dak.dakpanOverhang">
       
              <label class="default-form-field-label" for="pos">Dakpan overhang lengte achterkant</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.dak.dakpanOverhangAchter">
        
              <label class="default-form-field-label" for="pos">Gording shoor hoek</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.dak.gordingSchoorHoek">
        
              <label class="default-form-field-label" for="pos">Gording schoor offset</label>
              <input type="number" class="default-form-field-input" id="pos" v-model.trim="size.dak.gordingSchoorOffset">
            </div>

            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Dakgoot:</h1>
              <div class="spacer"></div>
              <span class="sizes-form-toggle-help">Actief in model</span>
              <input class="checkbox" type="checkbox" :id="`dakgoot-checkbox`+i" name="dakgoot-checkbox" v-model="size.dakgoot.enabled">
              <label :for="`dakgoot-checkbox`+i" class="checkbox-label sizes-form-checkbox">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.dakgoot.enabled ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('dakgoot', i)">
                <mdicon :name="groupshown[i].dakgoot ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].dakgoot ? '' : 'sizes-form-collapsed'">
              <label class="default-form-field-label" for="dakgootMeshName">Dakgoot mesh naam</label>
              <select class="default-form-field-input" v-model="size.dakgoot.meshName" id="dakgootMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="ringBalkY">Hoogte</label>
              <input type="number" class="default-form-field-input" id="ringBalkY" v-model.trim="size.dakgoot.y">

              <label class="default-form-field-label" for="ringBalkZ">Afstand tussen frame en goot</label>
              <input type="number" class="default-form-field-input BottomM" id="ringBalkZ" v-model.trim="size.dakgoot.offset">

              <label class="default-form-field-label" for="offsetAchter">Afstand tussen frame en goot achter</label>
              <input type="number" class="default-form-field-input BottomM" id="offsetAchter" v-model.trim="size.dakgoot.offsetAchter">

              <label class="default-form-field-label" for="ringBalkZ" title="De dakgootlengte is gelijk aan de breedte van de dakpannen/dakbeplating.">Dakgootlengte voor 2 regenpijpen</label>
              <input type="number" class="default-form-field-input BottomM" id="ringBalkZ" v-model.trim="size.dakgoot.twoPipesWidth">

              <label class="default-form-field-label" for="ringBalkZ" title="De dakgootlengte is gelijk aan de breedte van de dakpannen/dakbeplating.">Dakgootlengte voor 3 regenpijpen</label>
              <input type="number" class="default-form-field-input BottomM" id="ringBalkZ" v-model.trim="size.dakgoot.threePipesWidth">
            </div>

            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Wand:</h1>
              <div class="spacer"></div>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('wall', i)">
                <mdicon :name="groupshown[i].wall ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].wall ? '' : 'sizes-form-collapsed'">
              <label class="default-form-field-label" for="wallMeshName">Wand mesh naam</label>
              <select class="default-form-field-input" v-model="size.wall.meshName" id="wallMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="ringBalkY">Wand positie</label>
              <select class="default-form-field-input" @change="size.wall.position = $event.target.value">
                <option :selected="size.wall.position !== 'tussen' && size.wall.position !== 'buiten'" hidden :value="'tussen'">Selecteer een positie</option>
                <option :selected="size.wall.position === 'tussen'" :value="'tussen'">Tussen</option>
                <option :selected="size.wall.position === 'buiten'" :value="'buiten'">Buiten</option>
              </select>

              <label class="default-form-field-label" for="ringBalkZ">Wand plank dikte</label>
              <input type="number" class="default-form-field-input BottomM" id="ringBalkZ" v-model.trim="size.wall.wz">
            </div>

            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Omlijsting palen:</h1>
              <div class="spacer"></div>
              <span class="sizes-form-toggle-help">Actief in model</span>
              <input class="checkbox" type="checkbox" :id="`omlijstingPalen-checkbox`+i" name="trim-checkbox" v-model="size.omlijstingPalen">
              <label :for="`omlijstingPalen-checkbox`+i" class="checkbox-label sizes-form-checkbox">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.omlijstingPalen ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('omlijstingPalen', i)">
                <mdicon :name="groupshown[i].omlijstingPalen ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].omlijstingPalen ? '' : 'sizes-form-collapsed'">  
              <label class="default-form-field-label" for="omlijstingPalenDataMeshName">Dak mesh naam</label>
              <select class="default-form-field-input" v-model="size.omlijstingPalenData.meshName" id="omlijstingPalenDataMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>
            </div>

            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title">Veranda muur:</h1>
              <div class="spacer"></div>
              <span class="sizes-form-toggle-help">Actief in model</span>
              <input class="checkbox" type="checkbox" :id="`veranda-checkbox`+i" name="veranda-checkbox" v-model="size.veranda.enabled">
              <label :for="`veranda-checkbox`+i" class="checkbox-label sizes-form-checkbox">
                <mdicon name="check-bold" class="checkbox-label-icon" :class="size.veranda.enabled ? 'checkbox-label-icon-active' : ''" size="15" />
              </label>
              <button class="default-form-field-button sizes-form-header-button" @click="toggleGroup('veranda', i)">
                <mdicon :name="groupshown[i].veranda ? 'chevron-down' : 'chevron-up'" class="default-form-field-button-icon" size="30" />
              </button>
            </div>
            <div class="sizes-form" :class="groupshown[i].veranda ? '' : 'sizes-form-collapsed'">
              <label class="default-form-field-label" for="verandaMeshName">Veranda mesh naam</label>
              <select class="default-form-field-input" v-model="size.veranda.meshName" id="verandaMeshName">
                <option v-for="(meshName, i) in meshNames" :key="i" :value="meshName.id">{{ meshName.meshName }}</option>
              </select>

              <label class="default-form-field-label" for="ringBalkY">Hoogte</label>
              <input type="number" class="default-form-field-input" id="ringBalkY" v-model.trim="size.veranda.hoogte">

              <label class="default-form-field-label" for="ringBalkY">Dikte</label>
              <input type="number" class="default-form-field-input" id="ringBalkY" v-model.trim="size.veranda.dikte">

              <label class="default-form-field-label" for="ringBalkY">Extra breedte</label>
              <input type="number" class="default-form-field-input" id="ringBalkY" v-model.trim="size.veranda.extraBreedte">

              <label class="default-form-field-label" for="ringBalkY">Materiaal</label>
              <select v-model="size.veranda.material" class="default-form-field-input item">
                <option v-for="(material, j) in materials" :key="i+'-'+j" :value="material.id">{{material.name}}</option>
              </select>
            </div>

            <div class="sizes-form-header">
              <h1 class="default-form-field-label sizes-form-title" style="text-align: left">Breedtes:</h1>
              <div class="spacer"></div>
              <button class="default-form-field-button" @click="addBreedte(i)">
                <mdicon name="plus" class="default-form-field-button-icon" size="18" />
              </button>
            </div>

            <div class="breedtes-section">
              <div class="breedtes-scroll">
                <!-- Header: left columns, then dynamic Vak headers, then actions -->
                <div class="breedtes-header-grid" :style="{ gridTemplateColumns: buildBreedtesColumns(i) }">
                  <span class="colhead">Breedte</span>
                  <span class="colhead">Type</span>
                  <span class="colhead">Artikelnr</span>
                  <!-- spacer between Artikelnr and Vak # -->
                  <span class="colspacer colspacer-tight"></span>
                  <span class="colhead">Vak #</span>
                  <template v-for="k in getMaxVak(i)" :key="`vak-head-${i}-${k}`">
                    <span class="colhead vak-colhead">Vak {{ k }}</span>
                  </template>
                  <!-- spacer before Aan/uit -->
                  <span class="colspacer"></span>
                  <span class="colhead">Aan/uit</span>
                  <!-- spacer between Aan/uit and Acties -->
                  <span class="colspacer colspacer-mid"></span>
                  <span class="colhead">Acties</span>
                </div>

                <!-- Rows -->
                <div class="breedtes-row-grid" v-for="(breedte, j) in size.breedtes" :key="`breedtes-row-${i}-${j}`" :style="{ gridTemplateColumns: buildBreedtesColumns(i) }">
                  <!-- Left: main fields (4 cols) -->
                  <label class="breedte-regel-first" :class="(wallDistribution[i] && wallDistribution[i][j] && wallDistribution[i][j].valid) ? '' : 'breedte-regel-unvalid'">{{ breedte.breedte }}</label>

                  <select class="default-form-field-input breedte-regel select-type" v-model="breedte.bergingType">
                    <option hidden value="">Kies een type</option>
                    <option v-for="(type, b) in bergingTypes" :key="`berging_${i}_${j}_${b}`" :value="type.id">{{ type.name }}</option>
                  </select>

                  <div class="artikelmouseover">
                    <input type="text" class="default-form-field-input breedte-regel artikelnummer-input" maxlength="9" :class="{ 'red-border': showRedBorder }" :value="breedte.artikelNummer" @input="updateArtikelNummer($event, breedte)" @keydown="handleKeydown">
                    <div v-if="showNoSpaceWarning" class="no-space-warning">SPATIE niet toegestaan</div>
                  </div>

                  <!-- spacer between Artikelnr and Vak # -->
                  <div class="colspacer colspacer-tight"></div>
                  <div class="vakcount-inline">
                    <input type="number" min="1" max="99" class="default-form-field-input breedte-regel vakken-amount-input" :value="(wallDistribution[i] && wallDistribution[i][j]) ? wallDistribution[i][j].amount : 0" @input="addVak($event, i, j)" placeholder="0">
                  </div>

                  <!-- Dynamic Vak columns -->
                  <template v-for="k in getMaxVak(i)" :key="`vak-cell-${i}-${j}-${k}`">
                    <div class="vak-cell">
                      <template v-if="breedte.wallDivision[k-1]">
                        <input type="number" class="default-form-field-input vak-width-input" @input="recalculateVakken(i, j)" v-model.number="breedte.wallDivision[k-1].width" min="500" :max="size.maximaleMuurBreedte" step=".1">
                        <input class="checkbox" :id="`vak-fixed_${i}_${j}_${k-1}`" type="checkbox" v-model="breedte.wallDivision[k-1].bergingEnabled">
                        <label :for="`vak-fixed_${i}_${j}_${k-1}`" class="checkbox-label vak-check">
                          <mdicon name="check-bold" class="checkbox-label-icon" :class="breedte.wallDivision[k-1].bergingEnabled ? 'checkbox-label-icon-active' : ''" size="14" />
                        </label>
                      </template>
                    </div>
                  </template>

                  <!-- spacer before Aan/uit toggle -->
                  <div class="colspacer"></div>
                  <!-- Aan/uit toggle -->
                  <div class="aanuit-cell">
                    <input class="checkbox" type="checkbox" :id="`setting-enabled-checkbox_${i}_${j}`" v-model="breedte.enabled">
                    <label :for="`setting-enabled-checkbox_${i}_${j}`" class="checkbox-label sizes-form-checkbox" title="In/uit schakelen">
                      <mdicon name="check-bold" class="checkbox-label-icon" :class="breedte.enabled ? 'checkbox-label-icon-active' : ''" size="15" />
                    </label>
                  </div>

                  <!-- spacer between Aan/uit and Actions -->
                  <div class="colspacer colspacer-mid"></div>
                  <!-- Actions -->
                  <div class="breedtes-actions">
                    <button class="icon-btn" title="Kopieer" @click="copyBreedte(i, j)">
                      <mdicon name="plus" class="default-form-field-button-icon" size="18" />
                    </button>
                    <button class="icon-btn" title="Verwijder" @click="removeBreedte(i, j)">
                      <mdicon name="minus" class="default-form-field-button-icon" size="18" />
                    </button>
                  </div>
                </div>

                
              </div>
            </div>

            <!-- END: Full original sections -->

          </div>
        </div>
        </div>
      </div>

      

      <div class="default-form-button">
        <LoadingButton 
          @click="newModel ? create() : update()" 
          :loading="loading" 
          :disabled="!formValid"
        >
          {{ newModel ? 'Creëren' : 'Update' }}
        </LoadingButton>
      </div>

    </form>
  </div>
</template>

<script>
  import isEqual from 'lodash.isequal'
  import { mapGetters } from 'vuex'
  import LoadingButton from '@/components/inputs/buttons/LoadingButton.vue'
  import { ref, h } from 'vue'

  export default {
    name: "ModelDetailsV02",

    components: {
      LoadingButton
    },

    data () {
      return {
        id: null,
        loading: false,
        newModel: false,
        data: {
          name: "",
          type: {
            name: "",
            id: null,
          },
          infobox: {
            name: "",
            uiIndex: "",
            id: null,
          },
          infoboxIsOnline: false,
          category: {
            name: "",
            id: null,
          },
          online: false,
          maatwerk: {
            enabled: false,
            width: {
              min: 0,
              max: 0,
            },
            depth: {
              min: 0,
              max: 0,
            },
          },
          roofFinishes: [],
          wallFinishes: {
            wandBuiten: [],
            wandBinnen: [],
            tussenWandLinks: [],
            tussenWandRechts: [],
            topgevelBuiten: [],
            topgevelBinnen: [],
            topgevelLinks: [],
            topgevelRechts: [],
          },
          overstekKlosRound : false,
          availableOptions: {
            fundering: true,
            dakgoot: false,
            lichtstraten: true,
            ledverlichting: true
          },
          meshNames: {
            staander: "",
            poer: "",
            betonband: "",
            poerenplan: "",
            regelwerk: "",
            assets: "",
            assetsWandvak: "",
            asset: ""
          },
          wall: {
            minWallWidth: 500,
            needsSpaceForStaander: true
          },
          defaultSizes: []
        },
        model: {},
        widths: [],
        formValid: false,
        filteredTextures: null,
        allWallFinishes: [],
        groupshown: [],
        wallDistribution: [],
        wallTypesAndsides: [
          {id: 'wandBuiten', full: 'Wand Buiten', short: 'WBui'},
          {id: 'wandBinnen', full: 'Wand Binnen', short: 'WBi'},
          {id: 'tussenWandLinks', full: 'Tussenwand Links', short: 'TWL'},
          {id: 'tussenWandRechts', full: 'Tussenwand Rechts', short: 'TWR'},
          {id: 'topgevelBuiten', full: 'Topgevel Buiten', short: 'TGBui'},
          {id: 'topgevelBinnen', full: 'Topgevel Binnen', short: 'TGBi'},
          {id: 'topgevelLinks', full: 'Topgevel Links', short: 'TGL'},
          {id: 'topgevelRechts', full: 'Topgevel Rechts', short: 'TGR'},
        ],
        wallTypesAndsidesforDimensions: [
          {id: 'wandBuiten', full: 'Wand Buiten', short: 'WBui'},
          {id: 'wandBinnen', full: 'Wand Binnen', short: 'WBi'},
          {id: 'tussenWandLinks', full: 'Tussenwand Links', short: 'TWL'},
          {id: 'tussenWandRechts', full: 'Tussenwand Rechts', short: 'TWR'},
        ],
        wallDimensions: [
          'Hoogte wanden'
        ],
        userShortName: false,
        previewsServer: import.meta.env.VITE_VUE_APP_SERVER,
        // Search state
        searchTerm: '',
        lastSearchEl: null,
        lastSearchOriginalHtml: null,
        searchMatches: [],
        searchOriginals: [], // [{el, html}]
        searchIndex: 0,
        meshNameKeys: ['staander', 'poer', 'betonband', 'poerenplan', 'regelwerk', 'assets', 'assetsWandvak', 'asset'],
      }
    },

    setup() {
      const showRedBorder = ref(false)
      const showNoSpaceWarning = ref(false)
      return { showRedBorder, showNoSpaceWarning }
    },

  methods: {
      // Perform a search across visible section titles/labels; open collapsibles and scroll/highlight
      performSearch() {
        const qRaw = (this.searchTerm || '').trim()
        const q = qRaw.toLowerCase()
        // If empty, clear previous highlight and stop
        if (!q) {
          this.clearSearchHighlights()
          return
        }

        // Clear previous highlights before new search
        this.clearSearchHighlights()

        // Expand all groups to allow matching hidden sections, but only for the first depth card for clarity
        // Strategy: search across all depth cards; when we find a match, ensure its parent groups are opened
        const root = this.$refs.editorRoot
        if (!root) return

        // Candidate elements: section headers and labels within .sizes-form-header and .default-form-field-label
        const findTarget = () => {
          const candidates = root.querySelectorAll('.sizes-form-header .sizes-form-title, .default-form-field-label, .sizes-header-name')
          for (const el of candidates) {
            const text = (el.textContent || '').toLowerCase()
            if (text.includes(q)) { return el }
          }
          return null
        }

        let target = findTarget()
        // Fallback: if nothing found, open 'diepte' across all and try again
        if (!target && this.groupshown && this.groupshown.length) {
          // Determine from first card whether diepte is closed; toggle to open across all
          if (!this.groupshown[0].diepte) this.toggleGroup('diepte', 0)
          target = findTarget()
        }

        if (!target) return

        // If target is inside a collapsed group, open that group across all depth cards via toggleGroup
        // Strategy: open diepte first (if closed), then detect the nearest section header within the same depth card
        const depthCard = target.closest('.depth-card')
        const depthIndex = depthCard ? Number(depthCard.getAttribute('data-depth-index')) : -1

        let toggledAny = false
        const openIfCollapsed = (key) => {
          if (!key) return false
          if (depthIndex >= 0 && this.groupshown[depthIndex] && this.groupshown[depthIndex][key] === false) {
            this.toggleGroup(key, depthIndex)
            toggledAny = true
            return true
          }
          return false
        }

  // Always ensure 'diepte' is open when focusing content inside a card
  openIfCollapsed('diepte')

        // Map some common Dutch labels to section keys (header titles)
        const labelToKey = [
          { m: 'afmetingen', key: 'afmetingen' },
          { m: 'schoor', key: 'schoor' },
          { m: 'ringbalk tussen paal', key: 'ringbalkTussenPaal' },
          { m: 'extra ringbalk', key: 'extraRingBalk' },
          { m: 'ringbalk gording', key: 'ringBalkGording' },
          { m: 'ringbalk', key: 'ringBalk' },
          { m: 'gording', key: 'gording' },
          { m: 'extra balk op gording', key: 'extraBalkOpGording' },
          { m: 'extra plank achter', key: 'extraPlankAchter' },
          { m: 'overstekklos', key: 'overstekKlos' },
          { m: 'boeideel', key: 'boeideel' },
          { m: 'trim', key: 'trim' },
          { m: 'dakgoot', key: 'dakgoot' },
          { m: 'dak', key: 'dak' },
          { m: 'wand', key: 'wall' },
          { m: 'veranda', key: 'veranda' },
          { m: 'breedtes', key: null }, // not collapsible
        ]

        const lower = (s) => (s||'').toLowerCase()

        // Find nearest header title inside the same card that precedes the target in DOM order
        let headerKey = null
        if (depthCard) {
          const titles = Array.from(depthCard.querySelectorAll('.sizes-form-header .sizes-form-title'))
          let lastBefore = null
          for (const t of titles) {
            if (t.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING) {
              lastBefore = t
            }
          }
          const headerTitle = lastBefore ? lower((lastBefore.textContent || '').replace(/:$/, '').trim()) : ''
          for (const map of labelToKey) {
            if (headerTitle.includes(map.m)) { headerKey = map.key; break }
          }
        }
        openIfCollapsed(headerKey)

  // Collect and highlight ALL matches across the page
        const candidatesAll = root.querySelectorAll('.sizes-form-header .sizes-form-title, .default-form-field-label, .sizes-header-name')
        const esc = (s) => s.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')
        const rx = new RegExp(esc(this.searchTerm), 'ig')
        const matched = []
        const originals = []
        for (const el of candidatesAll) {
          const text = el.textContent || ''
          if (text.toLowerCase().includes(q)) {
            originals.push({ el, html: el.innerHTML })
            const wrapped = text.replace(rx, (m) => `<span class=\"search-text-mark\">${m}</span>`)
            el.innerHTML = wrapped
            el.classList.add('search-hit')
            matched.push(el)
          }
        }
        this.searchOriginals = originals
        this.searchMatches = matched
        // Determine current index as first target occurrence
        this.searchIndex = matched.findIndex((el) => el === target)
        if (this.searchIndex < 0) this.searchIndex = 0

        // Add current highlight class
        if (this.searchMatches.length) {
          this.searchMatches[this.searchIndex].classList.add('search-hit-current')
        }

        const doScrollAndHighlight = () => {
          if (!this.searchMatches.length) return
          const cur = this.searchMatches[this.searchIndex]
          cur.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
        }
        if (toggledAny) {
          this.$nextTick(() => doScrollAndHighlight())
        } else {
          doScrollAndHighlight()
        }
      },
      clearSearchHighlights() {
        // Remove element-level classes and restore original HTML
        if (this.searchOriginals && this.searchOriginals.length) {
          for (const { el, html } of this.searchOriginals) {
            if (el) { el.innerHTML = html; el.classList.remove('search-hit', 'search-hit-current') }
          }
        }
        if (this.lastSearchEl) {
          this.lastSearchEl.classList.remove('search-hit', 'search-hit-current')
          if (this.lastSearchOriginalHtml != null) {
            this.lastSearchEl.innerHTML = this.lastSearchOriginalHtml
          }
        }
        this.searchOriginals = []
        this.searchMatches = []
        this.lastSearchEl = null
        this.lastSearchOriginalHtml = null
        this.searchIndex = 0
      },
      nextMatch() {
        if (!this.searchMatches.length) return
        // Remove current marker
        if (this.searchMatches[this.searchIndex]) {
          this.searchMatches[this.searchIndex].classList.remove('search-hit-current')
        }
        this.searchIndex = (this.searchIndex + 1) % this.searchMatches.length
        this.focusCurrentMatch()
      },
      prevMatch() {
        if (!this.searchMatches.length) return
        if (this.searchMatches[this.searchIndex]) {
          this.searchMatches[this.searchIndex].classList.remove('search-hit-current')
        }
        this.searchIndex = (this.searchIndex - 1 + this.searchMatches.length) % this.searchMatches.length
        this.focusCurrentMatch()
      },
      focusCurrentMatch() {
        if (!this.searchMatches.length) return
        const el = this.searchMatches[this.searchIndex]
        // Ensure parent groups are opened before scrolling
        this.openParentsFor(el)
        this.$nextTick(() => {
          el.classList.add('search-hit-current')
          el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
        })
      },
      openParentsFor(target) {
        const root = this.$refs.editorRoot
        if (!root) return
        const depthCard = target.closest('.depth-card')
        const depthIndex = depthCard ? Number(depthCard.getAttribute('data-depth-index')) : -1
        if (depthIndex < 0) return
        let toggledAny = false
        const openIfCollapsed = (key) => {
          if (!key) return false
          if (this.groupshown[depthIndex] && this.groupshown[depthIndex][key] === false) {
            this.toggleGroup(key, depthIndex)
            toggledAny = true
            return true
          }
          return false
        }
        // open diepte
        openIfCollapsed('diepte')
        // Determine nearest header before the target
        const lower = (s) => (s||'').toLowerCase()
        const labelToKey = [
          { m: 'afmetingen', key: 'afmetingen' },
          { m: 'schoor', key: 'schoor' },
          { m: 'ringbalk tussen paal', key: 'ringbalkTussenPaal' },
          { m: 'extra ringbalk', key: 'extraRingBalk' },
          { m: 'ringbalk gording', key: 'ringBalkGording' },
          { m: 'ringbalk', key: 'ringBalk' },
          { m: 'gording', key: 'gording' },
          { m: 'extra balk op gording', key: 'extraBalkOpGording' },
          { m: 'extra plank achter', key: 'extraPlankAchter' },
          { m: 'overstekklos', key: 'overstekKlos' },
          { m: 'boeideel', key: 'boeideel' },
          { m: 'trim', key: 'trim' },
          { m: 'dakgoot', key: 'dakgoot' },
          { m: 'dak', key: 'dak' },
          { m: 'wand', key: 'wall' },
          { m: 'veranda', key: 'veranda' },
        ]
        const titles = Array.from(depthCard.querySelectorAll('.sizes-form-header .sizes-form-title'))
        let lastBefore = null
        for (const t of titles) {
          if (t.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING) {
            lastBefore = t
          }
        }
        const headerTitle = lastBefore ? lower((lastBefore.textContent || '').replace(/:$/, '').trim()) : ''
        for (const map of labelToKey) {
          if (headerTitle.includes(map.m)) { openIfCollapsed(map.key); break }
        }
      },
      // Synchronize expansion/collapse of a section across all depth cards
      toggleGroup(sectionKey, i) {
        // Determine the new state from the clicked card
        const next = !this.groupshown[i][sectionKey]
        // Apply the new state to all cards (mutate in-place to preserve reactivity)
        this.groupshown.forEach(g => { g[sectionKey] = next })
      },
      updateArtikelNummer(event, breedte) {
        const newValue = event.target.value.replace(/\s/g, '')
        if (newValue !== breedte.artikelNummer) {
          breedte.artikelNummer = newValue
        }
      },
      handleKeydown(event) {
        if (event.keyCode === 32) {
          event.preventDefault()
          this.showRedBorder = true
          this.showNoSpaceWarning = true
          setTimeout(() => {
            this.showRedBorder = false
            this.showNoSpaceWarning = false
          }, 2000)
        }
      },  
      setId () {
        this.id = this.$route.params.id

        if (this.id === 'new') {
          this.newModel = true
        }
      },

      async get () {
        await this.$store.dispatch('getModelsInfo')

        if (!this.newModel) {
          this.model = this.models.find((m) => m.id === this.id)
          this.data = JSON.parse(JSON.stringify(this.model))
        }
        // Rebuild UI-parallel arrays from scratch to keep indexes aligned
        this.groupshown = []
        this.wallDistribution = []
        this.data.defaultSizes.forEach(() => {
          this.groupshown.push({
            diepte: true,
            afmetingen: true,
            schoor: false,
            ringBalk: false,
            extraRingBalk: false,
            ringbalkTussenPaal: false,
            gording: false,
            extraBalkOpGording: false,
            extraPlankAchter: false,
            ringBalkGording: false,
            overstekKlos: false,
            boeideel: false,
            trim: false,
            dak: false,
            dakgoot: false,
            wall: false,
            veranda: false,
            breedtes: false,
          })
        })

        this.data.defaultSizes.forEach((diepte) => {
          const depth = []
          diepte.breedtes.forEach((breedte) => {
            depth.push({
              amount: breedte.wallDivision.length,
              valid: true,
            })
          })
          this.wallDistribution.push(depth)
        })
      },

      addSize () {
        this.groupshown.push({
          diepte: true,
          afmetingen: true,
          schoor: false,
          ringBalk: false,
          extraRingBalk: false,
          ringbalkTussenPaal: false,
          extraBalkOpGording: false,
          extraPlankAchter: false,
          gording: false,
          ringBalkGording: false,
          overstekKlos: false,
          boeideel: false,
          trim: false,
          dak: false,
          dakgoot: false,
          breedtes: [],
        })
        if (this.data.defaultSizes.length > 0) {
          const newDepth = JSON.parse(JSON.stringify(this.data.defaultSizes[this.data.defaultSizes.length - 1]))
          newDepth.breedtes = []
          this.data.defaultSizes.push(newDepth)
        } else {
          this.data.defaultSizes.push({
            enabled: true,
            diepte: 3270,
            hoogtePaal: 2300,
            maximaleMuurBreedte: 3480,
            staanderDikte: 145,
            poer: { z: 150, y: 600 },
            schoor: { enabled: true, sz: 530, sy: 530, sx: 65, sox: 205, wallMinWidth: 1500 },
            ringBalk: { y: 145, z: 145 },
            extraRingBalk: { enabled: false, gordingVorm: false, y: 95, z: 65, position: false },
            ringbalkTussenPaal: { enabled: false, y: 0, z: 0 },
            gordingBalk: { y: 145, z: 65, afstandTussenGordingen: 700, position: 2300, ox: 0, oy: 0, oyl: 0 },
            extraBalkOpGording: { enabled: false, y: 0, z: 0 },
            extraPlankAchter: { enabled: false, y: 0, z: 0, xBov: 0, xOnd: 0, yKop: 0 },
            ringBalkGording: { y: 145, z: 145 },
            overstekKlos: {
              enabled: true,
              z: 65,
              zp: 145,
              xb: 220,
              xo: 60,
              y: 145,
              yk: 70,
              position: { left: true, right: true },
              zDik: 65,
              zDikPaal: 145,
              klosVoEnabled: true,
              klosVoEditable: false,
              xBovVo: 220,
              xOndVo: 60,
              yHooVo: 145,
              yKopVo: 70,
              klosAcEnabled: true,
              klosAcEditable: false,
              xBovAc: 220,
              xOndAc: 60,
              yHooAc: 145,
              yKopAc: 70,
              klosLiEnabled: true,
              klosLiEditable: false,
              xBovLi: 220,
              xOndLi: 60,
              yHooLi: 145,
              yKopLi: 70,
              klosReEnabled: true,
              klosReEditable: false,
              xBovRe: 220,
              xOndRe: 60,
              yHooRe: 145,
              yKopRe: 70,
              outsideMarginPraag: 0,
              losseKopMaxHartAfstand: 0
            },
            boeideel: { enabled: true, y: 195, z: 25, position: 66, lijstEnabled: true, ly: 60, lz: 25, lijstPosition: false, dpy: 0, dpz: 0, dpOffsetKopkant: 0, dpOffsetGootzijde: 0, nokpositie: 0, byl: 0, bylAchter: 0, douglasPlanken: false },
            trim: { enabled: true, y: 40, z: 15, overhang: 8, color: "62d1675487ae1ef94728a5ff", position: 0 },
            dak: { offset: 0, helling: 0, hellingAchter: 0, overhang: 0, beschotDikte: 18, dakpanOverhang: 0, gordingSchoorHoek: 0, gordingSchoorOffset: 0 },
            dakgoot: { enabled: false, y: 0, offset: 0, offsetAchter: 0, twoPipesWidth: 0, threePipesWidth: 0 },
            wall: {
              position: "tussen",
              wz: 18,
              height: [
                { position: 'wandBuiten', value: 2300 },
                { position: 'wandBinnen', value: 2300 },
                { position: 'tussenWandLinks', value: 2300 },
                { position: 'tussenWandRechts', value: 2300 }
              ]
            },
            omlijstingPalen: false,
            omlijstingPalenData: {},
            breedtes: [],
          })
          this.wallDistribution.push([])
        }
      },

      removeSize (i) {
        this.data.defaultSizes.splice(i, 1)
        this.groupshown.splice(i, 1)
        if (this.wallDistribution[i]) this.wallDistribution.splice(i, 1)
      },

      addBreedte (i) {
        this.data.defaultSizes[i].breedtes.push({ breedte: 0, bergingType: '', artikelNummer: '', wallDivision: [], enabled: true })
        if (!this.wallDistribution[i]) this.wallDistribution[i] = []
        this.wallDistribution[i].push({ amount: 0, valid: false })
      },

      copyBreedte (i, j) {
        this.data.defaultSizes[i].breedtes.push(JSON.parse(JSON.stringify(this.data.defaultSizes[i].breedtes[j])))
        this.wallDistribution[i].push(JSON.parse(JSON.stringify(this.wallDistribution[i][j])))
      },

      async create () {
        this.loading = true
        try {
          await this.$store.dispatch('createModel', this.data)
        } catch (err) {
          console.log('error',err)
          this.loading = false
        }
        this.loading = false
      },

      async update () {
        // Capture current UI state (open sections + scroll) so we can restore it after refresh
        const _uiSnapshot = this.captureUIState()
        this.loading = true
        try {
          this.allWallFinishes.forEach((wallFinish, i) => {
            const tex = this.textures.find((texture) => texture.id === wallFinish.id)
            if (tex) tex.order = i + 1
          })
          await this.$store.dispatch('updateTextures', { remove: [], update: this.textures, insert: [] })
          await this.$store.dispatch('getTextures')
          this.filterTextures()

          const payload = JSON.parse(JSON.stringify(this.data))
          const kept = (payload.defaultSizes || []).filter(s => !s.__markedForDeletion)
          if (kept.length > 0) {
            for (let i = 1; i < kept.length; i++) {
              kept[i].wall.height = kept[0].wall.height
            }
          }
          kept.forEach(s => { delete s.__markedForDeletion; delete s.__isNewCopy })
          payload.defaultSizes = kept

          await this.$store.dispatch('updateModel', payload)
          await this.get()
          // Restore open/closed sections and scroll position
          this.restoreUIState(_uiSnapshot)
        } catch (err) {
          console.log(err)
        } finally {
          this.loading = false
        }
      },
      // Capture open/closed UI state and scroll positions
      captureUIState() {
        try {
          const openStates = (this.groupshown || []).map((g, idx) => {
            const size = this.data?.defaultSizes?.[idx]
            const diepte = size ? size.diepte : null
            return { diepte, state: { ...g } }
          })
          const pageY = typeof window !== 'undefined' ? window.scrollY : 0
          let frameTop = 0, frameLeft = 0
          const root = this.$refs?.editorRoot
          const frame = root ? root.querySelector('.depth-row-frame') : null
          if (frame) { frameTop = frame.scrollTop; frameLeft = frame.scrollLeft }
          return { openStates, pageY, frameTop, frameLeft }
        } catch (e) {
          return { openStates: [], pageY: 0, frameTop: 0, frameLeft: 0 }
        }
      },
      // Restore open/closed UI state and scroll positions after data refresh
      restoreUIState(snapshot) {
        if (!snapshot || !Array.isArray(snapshot.openStates)) return
        try {
          const used = new Set()
          for (let i = 0; i < (this.data?.defaultSizes?.length || 0); i++) {
            const diepte = this.data.defaultSizes[i]?.diepte
            let matchIdx = snapshot.openStates.findIndex((s, idx) => !used.has(idx) && s.diepte === diepte)
            if (matchIdx === -1 && i < snapshot.openStates.length && !used.has(i)) {
              matchIdx = i
            }
            if (matchIdx >= 0 && this.groupshown[i]) {
              const prev = snapshot.openStates[matchIdx].state || {}
              const curr = this.groupshown[i]
              Object.keys(curr).forEach(k => {
                if (Object.prototype.hasOwnProperty.call(prev, k)) curr[k] = prev[k]
              })
              used.add(matchIdx)
            }
          }
          // Restore scroll after DOM paints
          this.$nextTick(() => {
            try {
              if (typeof window !== 'undefined') {
                window.scrollTo({ top: snapshot.pageY || 0, behavior: 'auto' })
              }
              const root = this.$refs?.editorRoot
              const frame = root ? root.querySelector('.depth-row-frame') : null
              if (frame) {
                frame.scrollTop = snapshot.frameTop || 0
                frame.scrollLeft = snapshot.frameLeft || 0
              }
            } catch (_) { /* noop */ }
          })
        } catch (_) { /* noop */ }
      },

      filterTextures() {
        this.filteredTextures = this.textures.filter(texture => texture.textureType === "62e3e09a990280c3716422dd")
        this.allWallFinishes = this.textures.filter(texture => texture.textureType === "62cd857a7c20bc17b85af2cd")
      },

      addRoofFinish() {
        if(this.$refs.roofFinishSelector.value === "hidden"){return}
        this.data.roofFinishes.push(this.$refs.roofFinishSelector.value)
        this.$refs.roofFinishSelector.value = "hidden"
      },

      removeRoofFinish(i) {
        this.data.roofFinishes.splice(i, 1)
      },

      addWallFinish() {
        if(this.$refs.wallFinishSelector.value === "hidden"){return}
        if(this.data.wallFinishes.find(finish => finish.id === this.$refs.wallFinishSelector.value)){return}

        const objectToPush = { id: this.$refs.wallFinishSelector.value, enabled: {} }
        this.wallTypesAndsides.forEach(wallType => { objectToPush.enabled[wallType.id] = false })
        this.data.wallFinishes.push(objectToPush)
        this.$refs.wallFinishSelector.value = "hidden"
      },

      removeWallFinish(i) {
        this.data.wallFinishes.splice(i, 1)
      },

      changeWallfinish(event, positionId, wallFinish) {
        if(event.target.checked) {
          this.data.wallFinishes[positionId].push(wallFinish)
        } else {
          this.data.wallFinishes[positionId].splice(this.data.wallFinishes[positionId].indexOf(wallFinish), 1)
        }
      },

      addTrimColor(i) {
        let selectedRef = this.$refs['trimColorSelector'+i][0]
        if(selectedRef.value === "hidden"){return}
        this.data.defaultSizes[i].trim.color.push(selectedRef.value)
        selectedRef.value = "hidden"
      },

      removeTrimColor(i, c) {
        this.data.defaultSizes[i].trim.color.splice(c,1)
      },

      changeBoeilijstPosition(i, value) {
        this.data.defaultSizes[i].boeideel.lijstPosition = value=="true"
      },

      compare( a, b ) {
        if ( a.breedte < b.breedte ) return -1
        if ( a.last_nom > b.last_nom ) return 1
        return 0
      },

      addVak(event, diepte, breedte) {
        while(event.target.value > this.data.defaultSizes[diepte].breedtes[breedte].wallDivision.length) {
          this.data.defaultSizes[diepte].breedtes[breedte].wallDivision.push({width: 0, bergingEnabled: false})
        } 
        while(event.target.value < this.data.defaultSizes[diepte].breedtes[breedte].wallDivision.length) {
          this.data.defaultSizes[diepte].breedtes[breedte].wallDivision.pop()
        }
        if (!this.wallDistribution[diepte]) this.wallDistribution[diepte] = []
        if (!this.wallDistribution[diepte][breedte]) this.wallDistribution[diepte][breedte] = { amount: 0, valid: false }
        this.wallDistribution[diepte][breedte].amount = Number(event.target.value) || 0
        this.recalculateVakken(diepte, breedte)
      },

      setFixed(diepte, breedte, division) {
        this.data.defaultSizes[diepte].breedtes[breedte].wallDivision[division].bergingEnabled = !this.data.defaultSizes[diepte].breedtes[breedte].wallDivision[division].bergingEnabled
      },

      recalculateVakken(diepte ,breedte) {
        const staanderDikte = this.data.defaultSizes[diepte].staanderDikte
        let totalWidth = staanderDikte
        let valid = true
        this.data.defaultSizes[diepte].breedtes[breedte].wallDivision.forEach(vak => {
          if(vak.width) {
            totalWidth += vak.width
          }
          totalWidth += staanderDikte
          if(vak.width < 500 || vak.width > this.data.defaultSizes[diepte].maximaleMuurBreedte) {
            valid = false
          }
        })
        this.data.defaultSizes[diepte].breedtes[breedte].breedte = totalWidth
        if (!this.wallDistribution[diepte]) this.wallDistribution[diepte] = []
        if (!this.wallDistribution[diepte][breedte]) this.wallDistribution[diepte][breedte] = { amount: this.data.defaultSizes[diepte].breedtes[breedte].wallDivision.length, valid }
        else this.wallDistribution[diepte][breedte].valid = valid
      },

      removeBreedte(diepte, breedte) {
        this.data.defaultSizes[diepte].breedtes.splice(breedte,1)
        this.wallDistribution[diepte].splice(breedte,1)
      },

      moveTextureUp(i) {
        if (i === 0) return
        this.allWallFinishes.splice(i - 1, 0, this.allWallFinishes.splice(i, 1)[0])
      },

      moveTextureDown(i) {
        if (i === this.allWallFinishes.length - 1) return
        this.allWallFinishes.splice(i + 1, 0, this.allWallFinishes.splice(i, 1)[0])
      },

      // Copy a depth card adjacent and then sort by diepte
      copyDepth(i) {
        const src = this.data.defaultSizes[i]
        if (!src) return
        const clone = JSON.parse(JSON.stringify(src))
        delete clone.__markedForDeletion
        clone.__isNewCopy = true
        this.data.defaultSizes.splice(i + 1, 0, clone)
        // keep parallel arrays aligned
        if (this.groupshown[i]) {
          this.groupshown.splice(i + 1, 0, JSON.parse(JSON.stringify(this.groupshown[i])))
        } else {
          this.groupshown.splice(i + 1, 0, { diepte: true, afmetingen: true })
        }
        if (this.wallDistribution[i]) {
          this.wallDistribution.splice(i + 1, 0, JSON.parse(JSON.stringify(this.wallDistribution[i])))
        } else {
          this.wallDistribution.splice(i + 1, 0, [])
        }
        // After inserting, keep equal dieptes adjacent; overall ascending order
        this.sortDepthsByValue()
      },

      // Cancel a newly copied depth card
      cancelCopiedDepth(i) {
        const size = this.data.defaultSizes[i]
        if (!size || !size.__isNewCopy) return
        this.data.defaultSizes.splice(i, 1)
        if (this.groupshown[i]) this.groupshown.splice(i, 1)
        if (this.wallDistribution[i]) this.wallDistribution.splice(i, 1)
      },

      // When a depth value changes, re-sort to keep natural ordering
      onDepthChange(i) {
        const v = Number(this.data.defaultSizes[i]?.diepte)
        if (!Number.isFinite(v)) return
        this.sortDepthsByValue()
      },

      // Sort depths by their diepte value and keep parallel arrays in sync
      sortDepthsByValue() {
        const zipped = this.data.defaultSizes.map((size, idx) => ({
          size,
          group: this.groupshown[idx] || {},
          dist: this.wallDistribution[idx] || []
        }))
        zipped.sort((a, b) => {
          const av = Number(a.size?.diepte) || 0
          const bv = Number(b.size?.diepte) || 0
          if (av === bv) return 0
          return av < bv ? -1 : 1
        })
        this.data.defaultSizes = zipped.map(z => z.size)
        this.groupshown = zipped.map(z => z.group)
        this.wallDistribution = zipped.map(z => z.dist)
      },

      // Soft-delete toggle for depth steps
      toggleRemoveDepth(i) {
        const size = this.data.defaultSizes[i]
        if (!size) return
        size.__markedForDeletion = !size.__markedForDeletion
      },

      // Return the maximum wallDivision length across all breedtes for a given depth index
      getMaxVak(diepteIndex) {
        const size = this.data.defaultSizes[diepteIndex]
        if (!size || !size.breedtes || !size.breedtes.length) return 0
        return size.breedtes.reduce((max, b) => Math.max(max, (b.wallDivision ? b.wallDivision.length : 0)), 0)
      },

      // Build CSS grid-template-columns string for header and rows
      buildBreedtesColumns(diepteIndex) {
        const vakCols = this.getMaxVak(diepteIndex)
        // Left fixed columns tightened to pull Vak columns closer to "Vak #"
        const left = [
          '56px',   // Breedte label (tighter)
          '128px',  // Type select (tighter)
          '80px',   // Artikelnr
          '8px',    // tight spacer between Artikelnr and Vak #
          '46px'    // Vak count tightened further
        ]
        // Dynamic vak columns: tie to CSS var so header/rows stay in sync with input width
        const vak = Array.from({ length: vakCols }, () => 'var(--vak-col, calc(var(--vak-input-w, 9ch) + 28px))')
        // Add spacer before Aan/uit and between Aan/uit and Actions
        const spacerBefore = ['12px']
        const spacerMid = ['16px']
        // Right: Aan/uit + Actions tightened
        const right = ['40px', '64px']
        return [...left, ...vak, ...spacerBefore, 'var(--aanuit-col, 40px)', ...spacerMid, 'var(--actions-col, 64px)'].join(' ')
      },

    },


    computed: {
      ...mapGetters({
        models: "getModels",
        types: "getTypes",
        infoboxes: "getInfoboxes",
        categories: "getModelCategories",
        textures: "getTextures",
        materials: "getMaterials",
        colors: "getMenuColors",
        meshNames: "getMeshNames",
        glasTypes: "getMenuGlasTypes",
        bergingTypes: "getBergingTypes",
      }),
    },

    async created () {
      this.setId()
      await this.get()
      await this.$store.dispatch('getTextures')
      await this.$store.dispatch('getMaterials')
      await this.$store.dispatch('getMenuColors')
      await this.$store.dispatch('getMenuGlasTypes')
      await this.$store.dispatch('getMeshNames')
      this.filterTextures()

      if(this.data.defaultSizes.length) {
        this.wallTypesAndsidesforDimensions.forEach((wallType) => {
          if(!this.data.defaultSizes[0].wall.height.find(h => h.position === wallType.id)) {
            this.data.defaultSizes[0].wall.height.push({ position: wallType.id, value: 0 })
          }
        })
        // keep initial order by numeric depth
        this.sortDepthsByValue()
      }

      document.addEventListener('keydown', e => {
        if (e.ctrlKey && e.key === 's') {
          e.preventDefault();
          this.update()
        }
      });
    },

    watch: {
      data: {
        deep: true,
        immediate: false,
        handler () {
          if (this.newModel) {
            this.formValid = true
          } else {
            if (isEqual(this.model, this.data)) {
              this.formValid = false
            } else {
              this.formValid = true
            }
          }
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
/* Inherit base styles from Model.vue and only add layout for side-by-side depth cards */
.edit {
  display: flex;
  flex-direction: column;
  padding: 25px;
  align-items: center;
  min-width: min(100%, 800px);

  &-form {
    padding: 25px 0px;
    min-width: min(100%, 1200px);
  }
}

.sizes {
  display: flex;
  flex-direction: column;
}

.sizes-header {
  display: flex;
  margin-top: 16px;
  margin-bottom: 8px;
  gap: 8px;

  &-name { font-size: 1.1rem; font-weight: 500; }
}

/* Red remove button with inline text */
.remove-depth-button {
  display: inline-grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  gap: 4px;
  background-color: #d93025; /* solid red */
  border-color: #d93025;
  color: #ffffff;
  width: auto;         /* ensure not square */
  min-width: 120px;    /* make rectangular and readable */
  padding: 0 12px;     /* comfortable horizontal padding */
  white-space: nowrap; /* keep icon + text on one line */
}
.remove-depth-button:hover {
  background-color: #c6281f;
}
.remove-depth-button .default-form-field-button-icon { color: #ffffff; }
.remove-depth-button .btn-text { font-size: 12px; font-weight: 700; opacity: 1; color: #ffffff; }

/* remove vertical stripe pseudo-element */

.sizes-form { margin-left: 25px; margin-bottom: 10px; display: grid; grid-template-columns: var(--label-col-w, 300px) 1fr; gap: 10px; }
.sizes-form-collapsed { display: none; margin-bottom: 0px; }
.sizes-form-header {
  margin-top: 0px;
  text-align: left;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.sizes-form-title { display: inline; margin-left: 15px; font-weight: bold; margin-top: 0px; }

/* Helper text next to section enable toggles */
.sizes-form-toggle-help {
  font-size: 12px;
  color: #5f6b7a;
  font-weight: 600;
}

/* New layout */
.edit--wide {
  max-width: 100vw;
  width: 100%;
  padding: 0 16px;
  overflow-x: hidden; /* prevent page-level horizontal scroll due to rounding */
}

.edit-form {
  width: 100%;
}

.depth-row-frame {
  position: sticky;
  top: 110px; /* below the sticky header height */
  background: white;
  z-index: 50;
  /* Make the frame fit in the viewport so the horizontal scrollbar is visible without scrolling the page */
  max-width: 100%;
  height: calc(90vh - 110px - 16px); /* fill remaining viewport below header */
  overflow-x: auto; /* horizontal scroll on the frame */
  overflow-y: auto; /* vertical scroll within the frame */
  padding-bottom: 2px; /* give the scrollbar a little breathing room */
}

/* Header inside the frame: title on the left, actions on the right */
.depth-row-frame-header {
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0 6px 0;
  border-bottom: 1px solid rgba(0,0,0,0.06);
}
.depth-row-frame-title {
  font-size: 1.1rem;
  font-weight: 600;
}
/* Push actions to the far right inside the header */
.depth-row-frame-header .spacer { flex: 1 1 auto; }

/* Green add button next to the title */
.add-depth-button {
  background-color: #16a34a; /* green-600 */
  border-color: #16a34a;
  color: #ffffff;
  min-width: 200px;       /* make it broad enough for the label */
  padding: 0 14px;        /* add horizontal space */
  white-space: nowrap;    /* keep icon + text on one line */
  display: inline-grid;   /* align like other buttons */
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  gap: 6px;
}
.add-depth-button:hover {
  background-color: #15803d; /* green-700 */
  border-color: #15803d;
}
.add-depth-button .default-form-field-button-icon { color: #ffffff; }
.add-depth-button .btn-text { color: #ffffff; font-weight: 600; }

.depth-row-scroll {
  display: flex;
  flex-direction: row;
  /* Use a shared gap var for precise width calc */
  --depth-gap: 16px;
  gap: var(--depth-gap);
  overflow-x: visible; /* horizontal scrollbar lives on the frame */
  padding-bottom: 8px;
  scroll-snap-type: x mandatory;
}

.depth-card {
  background: #fff;
  border-radius: $radius-m-1;
  box-shadow: $shadow-m-0;
  border: 1px solid rgba(0,0,0,0.08);
  padding: 12px 12px 16px 12px;
  box-sizing: border-box; /* include border in width calc to avoid overflow */
  /* Exactly 3 cards per viewport without scroll; scroll appears only when >3 */
  flex: 0 0 auto;
  width: calc((99% - (var(--depth-gap) * 2)) / 3 - 0.5px); /* subtract a hair to avoid rounding overflow */
  max-width: calc((100% - (var(--depth-gap) * 2)) / 3 - 0.5px);
  scroll-snap-align: start;
}

/* Soft-deleted depth card appearance (inspired by TexturesGrouped */
.depth-card.depth-card-deleted {
  background: #fff1f1;
  border-color: #d63939;
  box-shadow: 0 0 0 2px rgba(214,57,57,.25);
}

/* Newly copied depth card (warning/orange) */
.depth-card.depth-card-copied {
  background: #fff9ed;
  border-color: #f3b95c;
  box-shadow: 0 0 0 2px rgba(243,185,92,.25);
}

.cancel-depth-button {
  display: inline-grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #d63939;
  border-radius: 6px;
  color: #b91c1c; /* deeper red for text */
  background: #fff5f5;
  font-weight: 700;
}
.cancel-depth-button:hover {
  background: #ffe2e2;
  border-color: #b91c1c;
}

/* Small badge mirroring grouped textures style */
.depth-badge-delete {
  margin-left: 8px;
  align-self: center;
  background: #d63939;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 6px;
  border-radius: 4px;
  letter-spacing: .5px;
}

/* Cancel button for copied state (orange theme) */
.cancel-copy-button {
  display: inline-grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #e99f27;
  border-radius: 6px;
  color: #a15d00;
  background: #fff6ea;
  font-weight: 700;
}
.cancel-copy-button:hover {
  background: #ffeacd;
  border-color: #cc7f00;
}

/* Keep card inner spacing default unless explicitly set in sections */
/* (No global override for .depth-card .sizes-form) */

/* Deprecated: old Breedtes layout helpers (left for reference; no longer used) */
// .breedtes-wrapper { max-width: 100%; overflow-x: auto; }
// .breedtes-grid { min-width: 0; }

/* New, cleaner Breedtes table layout */
.breedtes-section { margin-left: 15px; }
/* wallfinishes-grid shares the page flow; no extra left margin here so:
  - left list sits under the label column
  - table starts aligned with the input/checkbox column */
.wallfinishes-grid {
  margin-left: 0;
  margin-top: 0px;
}
.wallfinishes-grid + .wallfinishes-grid { margin-top: 38px; }
/* Unified horizontal scroll for header and rows */
.breedtes-scroll {
  overflow-x: auto;
  padding-bottom: 6px;
  /* Control vak input and column widths via CSS vars so header and rows match */
  --vak-input-w: 9ch; /* show 9 characters (spin buttons considered) */
  --vak-col: calc(var(--vak-input-w) + 28px); /* input + 2 checkbox columns (14px + 14px) */
}
.breedtes-header-grid { display: grid; gap: 4px; align-items: center; min-width: max-content; margin-bottom: 4px; }
.breedtes-row-grid { display: grid; gap: 4px; align-items: center; min-width: max-content; margin-bottom: 6px; }
.breedtes-footer { text-align: left; padding-left: 0; }
.breedtes-actions { display: grid; grid-auto-flow: column; grid-auto-columns: max-content; gap: 4px; align-items: center; justify-content: start; }
.colspacer { display: block; width: 100%; height: 1px; }
.colhead { font-weight: 600; font-size: 12px; color: #333; white-space: nowrap; text-align: left; }
.vak-colhead { text-align: left; }
.icon-btn { width: 28px; height: 28px; display: inline-grid; place-items: center; border: 1px solid rgba(0,0,0,0.1); border-radius: 6px; cursor: pointer; background: #fff; }
.icon-btn:hover { background: #f5f5f5; }
.remove-breedte { width: 28px; height: 28px; display: inline-grid; place-items: center; border: 1px solid rgba(0,0,0,0.1); border-radius: 6px; cursor: pointer; background: #fff; }
.remove-breedte:hover { background: #f5f5f5; }
.vakcount-inline { display: inline-grid; grid-template-columns: max-content; gap: 0; align-items: center; }
.select-type { height: 30px; padding-top: 0; padding-bottom: 0; }
/* Compact vak cell: input + checkbox tightly inline */
.vak-cell { display: inline-grid; grid-template-columns: var(--vak-input-w, 6ch) 14px 14px; gap: 0px; align-items: center; width: 100%; }
.row-enabled { display: grid; grid-template-columns: 16px 16px; gap: 2px; align-items: center; }

/* Validation state */
.breedte-regel-unvalid { color: #b00020; font-weight: 600; }

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 100;
  margin-bottom: 14px;
  padding: 8px 24px 8px 16px; /* more space on the right to push Update further right */
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.sticky-header-left {
  display: inline-grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  gap: 16px; /* more space between Oude editor and title */
  align-items: center;
  margin-right: 24px; /* extra gap between title and Update button */
}
.sticky-header-button {
  display: inline-grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  gap: 10px;
  align-items: center;
  margin-left: auto; /* push fully to the right */
}

/* Neutral/system style button for secondary navigation (e.g., Oude editor) */
.system-button {
  background: #f5f5f7;
  color: #1f2937;
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 6px;
  height: 30px;   /* lower button height */
  padding: 0 10px; /* tighter horizontal padding */
  cursor: pointer;
}
.system-button:hover { background: #ececef; }
.system-button .btn-text { font-weight: 600; line-height: 1; }

/* Tighten title size to reduce header height slightly */
.sticky-header h1 { font-size: 20px; line-height: 1.2; margin: 0; }

/* Search UI in header */
.search-input {
  height: 30px;
  padding: 0 10px;
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 6px;
  min-width: 220px;
}
.search-button { height: 30px; }

/* Highlight target after search */
.search-hit {
  outline: 2px solid #16a34a;
  outline-offset: 2px;
  background: rgba(22, 163, 74, 0.08);
  transition: background 0.3s ease;
}
.search-text-mark {
  background: #fffb91; /* soft yellow highlight */
  padding: 0 2px;
  border-radius: 3px;
}
/* Emphasize the currently focused match */
.search-hit-current {
  outline-color: #1d4ed8; /* blue-700 */
  background: rgba(29, 78, 216, 0.08);
}

/* Navigation buttons next to search input */
.nav-button {
  height: 30px;
  min-width: 30px;
  display: inline-grid;
  place-items: center;
}

.optionslist { display: block; width: 100%; margin-top: -10px; margin-left: 32px; }
.optionslist-item {
  display: grid;
  grid-template-columns: calc(var(--label-col-w, 300px) - 32px) 1fr; /* align with nested label width */
  align-items: center;
  gap: 10px;
  padding: 4px 0;
}
.optionslist .default-form-field-label { text-align: left; }
/* Sub-label appearance for items under 'Opties:' */
.optionslist .default-form-field-label,
.sizes .sizes-toggle .default-form-field-label {
  font-size: 13px;          /* slightly smaller than section title */
  color: #5f6b7a;           /* muted text to indicate subordinate hierarchy */
  font-weight: 600;         /* semi-bold for readability */
  padding-left: 0px;       /* indent label to align with nested rows */
}
/* Keep the title labels bold/primary — no change needed there */
.optionslist-check { display: grid; grid-auto-flow: column; grid-auto-columns: max-content; gap: 6px; align-items: center; justify-content: start; }
.optionslist-checkbox { top: -3px; position: relative; }

.rotated-header {
  display: inline-block;
  transform: rotate(-60deg);
  transform-origin: left bottom;
  white-space: nowrap;
}

.texture-order { margin-top: 2px; }
.texture-order-button { padding-left: 3px; }

.error { color: red; font-weight: bold; }

/* Breedtes tightening and alignment */
.breedte-regel { height: 20px; }
select.breedte-regel { height: 30px; }
.artikelnummer-input { width: 9ch; max-width: 9ch; }

.vakken-amount-input { width: 3ch; max-width: 3ch; text-align: right; }

.vak-width-input {
  width: var(--vak-input-w, 9ch); /* show 9 characters */
  max-width: var(--vak-input-w, 9ch);
  padding: 2px 4px; /* minimize horizontal padding to keep compact */
  border-radius: 5px 0 0 5px;
  box-sizing: border-box;
}
.colspacer-tight { width: 8px; height: 1px; }
.aanuit-cell { display: inline-grid; grid-template-columns: 16px 16px; gap: 1px; align-items: center; }

/* Checkbox tight to number */
.vak-check { margin-left: 0; display: inline-flex; }

.breedte-regel-header {
  white-space: nowrap;
}
.breedte-regel-first { min-width: 64px; }

.artikelmouseover { position: relative; }
.no-space-warning {
  position: absolute;
  top: 100%;
  left: 0;
  font-size: 11px;
  color: #c00;
}

/* Lock button next to per-vak input */
.vak-lock {
  width: 30px;
  height: 30px;
  border-radius: 0 5px 5px 0;
  vertical-align: top;
  cursor: pointer;
  border: 2px solid $c-primary;
}
.vak-lock-selected { background-color: $c-primary; }
.vak-lock-icon { color: $c-primary; }
.vak-lock-icon-selected { color: $c-secondary; }

/* Cap generic input widths to avoid horizontal overflow (without touching Breedtes-specific fields) */
.default-form-field > input.default-form-field-input,
.default-form-field > select,
.default-form-field > textarea.default-form-field-input {
  max-width: 420px;
}

.sizes .default-form-field-input,
.sizes select,
.sizes textarea.default-form-field-input {
  max-width: 420px;
}

.sizes-form .default-form-field-input,
.sizes-form select,
.sizes-form textarea.default-form-field-input {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  justify-self: start;
}

/* Utility: if a row needs two controls side-by-side, wrap them in a container with this class */
.sizes-form-inline-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

/* Align top introductory fields into a neat 2-col grid (label | control) */
.intro-fields {
  display: grid;
  grid-template-columns: 1fr; /* stack rows, per-row grid aligns content */
  gap: 8px;
  margin-bottom: 12px;
  /* expose a shared input width for alignment use elsewhere */
  --intro-input-w: 420px;
  /* shared label column width to align with wallfinishes left column */
  --label-col-w: 300px;
}
.intro-fields > .default-form-field {
  display: grid;
  grid-template-columns: var(--label-col-w, 300px) 1fr; /* fixed label column to align inputs */
  gap: 10px;
  align-items: center;
}
.intro-fields .default-form-field-label {
  margin: 0; /* remove extra margin to keep row tight */
}
.intro-fields .checkbox-label {
  margin: 0;
}

.inline-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.inline-checkbox-group {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.checkbox-help-text {
  font-size: 12px;
  color: #8791a1;
  line-height: 1.4;
}

.default-form-field--two-col {
  display: grid;
  grid-template-columns: var(--label-col-w, 300px) 1fr;
  gap: 10px;
  align-items: center;
}

.intro-fields .default-form-field--nested {
  margin-left: 32px;
  grid-template-columns: calc(var(--label-col-w, 300px) - 32px) 1fr;
}

.intro-fields .default-form-field--nested .default-form-field-label {
  font-size: 13px;
  color: #5a6574;
}

.intro-fields .standaardmaten-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.intro-fields .standaardmaten-help {
  font-size: 12px;
  color: #8791a1;
  line-height: 1.4;
}

/* Ensure top selects match Naam input width exactly */
.intro-fields .default-form-field-input {
  width: var(--intro-input-w, 420px);
  max-width: var(--intro-input-w, 420px);
  box-sizing: border-box;
}

/* Dakafwerkingen alignment */
.sizes-roof .roof-grid {
  display: grid;
  grid-template-columns: var(--label-col-w, 300px) 1fr; /* match intro-fields label width */
  gap: 10px;
  align-items: start;
}
.sizes-roof .roof-label {
  margin-top: 6px;
}
.sizes-roof .roof-list {
  display: grid;
  gap: 6px;
  justify-items: start; /* keep items aligned left */
}
.sizes-roof .rooffinish-item {
  display: grid;
  grid-template-columns: var(--intro-input-w, 420px) 32px; /* fixed input width + fixed button column */
  gap: 6px;
  align-items: center;
  justify-content: start;
  width: fit-content;
  text-align: left; /* ensure all contained text aligns left */
}
.sizes-roof .roof-add {
  display: grid;
  grid-template-columns: var(--intro-input-w, 420px) max-content; /* align add button with remove buttons */
  gap: 6px;
  align-items: center;
  justify-content: start;
  margin-top: 6px;
  width: fit-content;
}

/* Ensure button height aligns with input height for tidy rows */
.sizes-roof .rooffinish-item .default-form-field-button,
.sizes-roof .roof-add .default-form-field-button {
  height: 30px;
  min-width: 32px;
  display: inline-grid;
  place-items: center;
}

/* Ensure left column in Wand afwerkingen aligns text to the left */
.wallfinishes-left {
    text-align: left;
    overflow-x: hidden; /* prevent horizontal scrollbar */
}
/* Align the 'Gebruik korte naam' row like other form rows */
.sizes .sizes-toggle {
  display: grid;
  grid-template-columns: var(--label-col-w, 300px) max-content; /* keep toggle close to label */
  gap: 10px;
  align-items: center;
  margin: 0 0 0px 0;
}
  /* Normalize table spacing to avoid phantom rows/space */
  .wallfinishesTable {
    border-collapse: collapse;
    border-spacing: 0;
    width: max-content;
    table-layout: fixed;
  }
  /* Use consistent heights for header and rows */
  .wallfinishes-grid { --wf-head-h: 118px; --wf-row-h: 32px; }
  .wallfinishesTable thead th {
    position: relative;
    height: var(--wf-head-h, 32px);
    padding: 0;
    vertical-align: bottom;
    white-space: normal;
    overflow: visible;
  }
  .wallfinishesTable tbody td {
    height: var(--wf-row-h, 32px);
    padding: 0 2px;
    vertical-align: middle;
    white-space: normal;
  }
  .wallfinishesTable thead th:not(.actions-col),
  .wallfinishesTable tbody td {
    width: var(--wf-col-w, 44px);
    min-width: var(--wf-col-w, 44px);
  }
  .wallfinishesTable tbody td.actions-cell { width: auto; min-width: 0; }
  .wallfinishesTable .inline-checkbox { gap: 4px; }
  .wallfinishesTable thead th:not(.actions-col) .rotated-header {
    position: absolute;
    bottom: 0;
    left: 36px;
    transform: rotate(-62deg);
    transform-origin: left bottom;
    white-space: nowrap;
  }
  .wallfinishesTable th.actions-col {
    white-space: nowrap;
    font-size: 14px;
    font-weight: 400;
    font-style: italic;
  }
  .actions-cell { white-space: nowrap; }
  .inline-actions { display: inline-grid; grid-auto-flow: column; grid-auto-columns: max-content; gap: 6px; align-items: center; }
  /* Remove extra margins that cause a blank line after last item */
  .wallfinishes-left-item,
  .wallfinishes-right-item {
    margin: 0; /* align exactly with table row height */
    height: var(--wf-row-h, 32px);
    display: flex;
    align-items: center;
  }
  /* Reduce header spacer height to match table header */
  .wallfinishes-left > div:first-child,
  .wallfinishes-right > div:first-child {
    height: var(--wf-head-h, 32px) !important;
  }
  .wallfinishes-left .breedte-regel-first {
    display: block;
    padding-left: 32px;
    font-size: 13px;
    color: #5f6b7a;
    font-weight: 600;
  }

/* Ensure roof list text looks like inputs but doesn't stretch full width */
.sizes-roof .roof-list .default-form-field-input,
.sizes-roof .roof-add .default-form-field-input {
  text-align: left;
  display: inline-block;
  width: var(--intro-input-w, 420px);
  max-width: var(--intro-input-w, 420px);
  box-sizing: border-box; /* ensure padding/border included so widths match */
  margin: 0;
}

</style>